import moment from "moment-timezone"
import { CTF_SETTINGS_FIELD, ctfCoordinatorFromExerciseSettings, ctfFormatOptions, isCtfScenario } from "./ctfExercise.js"
import { exerciseTypeOf, groupExerciseType } from "./exerciseType.js"
import { v4 as uuidV4 } from "uuid"
import {
  FIELD_GROUP_TYPES,
  FIELD_TYPES,
  OTHER_OPTION_PLACEHOLDER_VALUE,
  VALIDATION_TYPES,
  tableGroupValueIdKey,
  validAPINodeErrorMessage,
  validAPINodeFieldMaxLength,
  validNameErrorMessage,
  validNameFieldMaxLength,
  validTextErrorMessage,
} from "../components/common/Dynamic-Form/dynamic-form.constants.js"
import logger from "../Logger"
import {
  DATE_FORMAT,
  GAME_MODES,
  MAX_INPUT_LIMIT,
  MAX_PRIVATE_INPUT_LIMIT,
  CLIENT_BASE_URL,
  ROUTE_PATHS,
  TABLE_GAME_ARCHIVED_FILTER,
  TABLE_GAME_FILTERS,
  TABLE_GAME_GROUP_BY_FILTER,
  TABLE_GAME_SCHEDULE_ACTION,
  TABLE_GAME_STATUS_FILTER,
  TABLE_GAME_TIME_FILTER,
  TABLE_SORT_ORDER,
  TIME_FORMAT,
  defaultSessionNLPModel,
  defaultSessionNLPModelPromptMatchedActionsPrescreen,
  defaultSessionNLPModelPromptMatchedActionsPrescreenDisplayName,
  nlpModelTypeLocal,
} from "../utilities/constants.js"
import { asNumber, getReplaneConfig } from "../utils/replaneClient"
import { isManager, isSponsor, isTrainer, userEmail } from "./authUtils.js"
import { getSession, getSessionGroupReports, sessionTimeSortParms } from "./data.js"
import { gameScenarioListFromData, gameScenarioVariableIsPrivate } from "./scenarioUtils.js"
import { booleanFromString, encodeRFC3986URIComponent } from "./uiUtils.js"
import { resolveSoloRolesSync, scenarioRoleCandidates } from "./roleMatchUtils.js"
import { memberPrimaryEmail } from "./participantDisplay.js"

const pageLogger = logger.child({ module: "gameSessionUtils" })

const sessionRoles = (session) => {
  return session?.roleAssignments ? Object.keys(session.roleAssignments) : []
}

const userIdForEmail = (session, email) => {
  let userId = null
  if (!session || !email) {
    return userId
  }

  for (const [cacheId, user] of Object.entries(session?.userCache ?? {})) {
    if (user?.email === email) {
      userId = cacheId
      break // for
    }
  }

  return userId
}

const userNameForEmail = (session, email) => {
  let userName = null
  if (!session || !email) {
    return userName
  }

  for (const [cacheId, user] of Object.entries(session?.userCache ?? {})) {
    if (user?.email === email) {
      userName = user?.name
      break // for
    }
  }

  return userName
}

// Type fields every exercise row carries (CTF reports spec §1): the resolved type for the Type
// filter/badge, the CTF coordinator flag and the CTF result summary (points + rank) the Score
// column shows. `members` (a group's child games) decide a group's type when the server sends none.
// A CTF group's list Score: the top member score and how many players have a result (the members'
// own ctfSummary rows); null until any member has one.
export const ctfGroupTop = (members) => {
  const sums = (members ?? []).map((m) => m?.ctfSummary).filter((s) => s && typeof s.score === "number")
  if (!sums.length) return null
  return { score: Math.max(...sums.map((s) => s.score)), players: sums.length }
}

const exerciseTypeFields = (exercise, members = null) => {
  const own = {
    exerciseType: exercise?.exerciseType ?? null,
    ctfCoordinator: exercise?.ctfCoordinator ?? null,
    ctfLive: exercise?.ctfLive === true,
    guidanceMode: exercise?.guidanceMode ?? null,
  }
  let resolved = exerciseTypeOf(own)
  if (!exercise?.exerciseType && Array.isArray(members) && members.length) {
    const memberTypes = members.map((m) => exerciseTypeOf({ ...m, guidanceMode: m?.guidanceMode ?? exercise?.guidanceMode }))
    // A live CTF flag on the group outranks members that carry no coordinator in the list payload.
    resolved = own.ctfLive ? "ctf" : groupExerciseType(memberTypes)
    if (resolved === "assessment" && own.guidanceMode === "guided") resolved = "training"
  }
  return {
    exerciseType: own.exerciseType,
    exerciseTypeResolved: resolved,
    ctfCoordinator: own.ctfCoordinator,
    ctfSummary: exercise?.ctfSummary ?? null,
  }
}

export const sessionParticipantEmails = (session) => {
  return session?.allPlayerEmails ?? []
}

export const sessionParticipantTitles = ({ session, email }) => {
  let role = null
  if (email && session) {
    // Get all roles for the user, deduping identical labels. A whole-incident member (e.g. an
    // Incident Commander whose typed role matched no scenario seat) is seated into every scenario
    // role, so this email maps to several role keys that all resolve to the same title — joining
    // raw produced "Incident Commander, Incident Commander, Incident Commander". Distinct roles
    // still all show.
    const userRoles = [
      ...new Set(
        Object.entries(session.roleAssignments ?? {})
          ?.filter(([role, thisRoleEmail]) => thisRoleEmail === email)
          ?.map(([role, thisRoleEmail]) => session.roleAssigneeTitles?.[role] ?? role) ?? []
      ),
    ]
    role = userRoles.length ? userRoles.join(", ") : null
  }
  return role
}

const sessionParticipantIdsAndEmails = (session) => {
  const allPlayerEmails = sessionParticipantEmails(session)
  const participants = []

  for (const playerEmail of allPlayerEmails) {
    const playerId = userIdForEmail(session, playerEmail)
    if (playerId) {
      participants.push({ user_id: playerId, email: playerEmail })
    }
  }
  return participants
}

export const sessionParticipantNames = (session) => {
  const allPlayerEmails = sessionParticipantEmails(session)
  const participants = []

  for (const playerEmail of allPlayerEmails) {
    const playerName = userNameForEmail(session, playerEmail)
    if (playerName) {
      participants.push({ user_name: playerName })
    }
  }
  return participants
}

export const sessionSpectatorEmails = (session) => {
  const allSpectators = session?.spectators ?? []
  const specEmails = allSpectators.map((spectatorObj) => spectatorObj.email)
  return specEmails
}

const sessionHasExpandedAIEnabled = (session) => {
  return session?.currentState?.expandedAIEnabled === true
}

export const sessionShouldUseChatbotAI = (session) => {
  return sessionHasExpandedAIEnabled(session)
}

const sessionSpectatorIds = (session) => {
  const allSpectators = session?.spectators ?? []
  const spectators = []

  for (const spectatorObj of allSpectators) {
    const playerId = userIdForEmail(session, spectatorObj.email)
    if (playerId) {
      spectators.push({ user_id: playerId })
    }
  }
  return spectators
}

const sessionReviewerEmails = (session) => {
  return session?.reviewers ?? []
}

export const sessionIsSinglePlayer = ({ session }) => {
  const allPlayers = session?.allPlayerEmails ?? []
  const collabSpectatorCount = (session?.spectators ?? []).filter(
    (spectatorObj) => spectatorObj.canChat === true || spectatorObj.canPlay === true
  ).length
  return allPlayers.length <= 1 && collabSpectatorCount === 0
}

export const userMayChatInSession = ({ session, user }) => {
  let userMayChat = false

  // Chat disabled for completed games
  if (gameStatusFor(session?.status) === TABLE_GAME_STATUS_FILTER.FINISHED) {
    return false
  }

  // Managers and sponsors may always chat
  if (isManager(user) || isSponsor(user)) {
    // Respect the chatEnabled flag even for managers/sponsors
    if (session?.chatEnabled === false) {
      return false
    }
    return true
  }

  // Chat disabled if chatEnabled flag is false
  if (session?.chatEnabled === false) {
    return false
  }

  const thisUserEmail = user?.email
  if (!thisUserEmail) {
    return false // Email required
  }

  // Get list of 'collaborating spectators' (canChat or canPlay enabled)
  const allSpectators = session?.spectators ?? []
  const collabEmails = allSpectators
    .filter((spectatorObj) => spectatorObj.canChat === true || spectatorObj.canPlay === true)
    .map(({ email }) => email)

  // Get list of all players
  const allPlayers = session?.allPlayerEmails ?? []

  // Check if user is a player w/ collaborators or if there are more than one player
  if (collabEmails.length > 0 || allPlayers.length > 1) {
    if (collabEmails.includes(thisUserEmail) || allPlayers.includes(thisUserEmail)) {
      userMayChat = true
    }
  }

  return userMayChat
}

// Note: returns null for multi-role games
export const singleRoleSessionPlayerInfo = (session) => {
  let singleRolePlayer = null
  if (session?.roleAssignments) {
    const roleEntries = Object.entries(session.roleAssignments)
    if (roleEntries.length === 1) {
      const roleName = roleEntries[0][0]
      singleRolePlayer = {
        role: roleName,
        email: roleEntries[0][1],
      }

      // Name, Title
      singleRolePlayer.name = session?.roleAssigneeNames?.[roleName]
      singleRolePlayer.title = session?.roleAssigneeTitles?.[roleName]
    }
  }
  return singleRolePlayer
}

export const gameStatusFor = (rawValue) => {
  let status
  switch (true) {
    case rawValue === "ONGOING":
      status = TABLE_GAME_STATUS_FILTER.ONGOING
      break
    case rawValue === "SCHEDULED":
      status = TABLE_GAME_STATUS_FILTER.SCHEDULED
      break
    case rawValue === "FINISHED":
      status = TABLE_GAME_STATUS_FILTER.FINISHED
      break
    default:
      status = TABLE_GAME_STATUS_FILTER.ONGOING
      break
  }
  return status
}

const gameScheduleForMillisecondsSinceEpoch = (msSinceEpoch) => {
  const schedule = {
    start_date: "----------",
    start_time: "--:----",
    // Raw epoch rides along for display-only derivations (compact date, relative age)
    epochMs: msSinceEpoch ?? null,
  }

  if (msSinceEpoch) {
    const started = moment(new Date(msSinceEpoch))
    schedule.start_date = started.format("MM-DD-YYYY")
    schedule.start_time = started.format("hh:mmA")
  }

  return schedule
}

const gameScheduleForSession = (session) => {
  if (session?.dispatchTime) {
    return gameScheduleForMillisecondsSinceEpoch(session.dispatchTime)
  } else if (session?.scheduledTime) {
    return gameScheduleForMillisecondsSinceEpoch(session.scheduledTime)
  }

  // Default
  return gameScheduleForMillisecondsSinceEpoch(null)
}

const gameScheduleForSessionGroup = (sessionGroup) => {
  // Check first batch's scheduledTime
  const scheduledTime = sessionGroup?.batches?.[0]?.scheduledTime
  if (scheduledTime) {
    return gameScheduleForMillisecondsSinceEpoch(scheduledTime)
  }

  // Default
  return gameScheduleForMillisecondsSinceEpoch(null)
}

// Construct a game list (groups + games) using the provided data
export const gameListFrom = ({ scenarios, sessionGroups, orgs }) => {
  let entries = []
  // Add entries for all session groups
  sessionGroups?.forEach((sessionGroup) => {
    // Get entries from group and update remaining sessions
    const groupEntries = gameListEntriesFromSessionGroup({
      sessionGroup,
      scenarios,
      orgs,
    })
    entries.push(...groupEntries)
  })
  return entries
}

/**
 * Transform /getExercises response into the flat game list format expected by GameList.
 * Also builds a userCache from participant/spectator data.
 */
export const gameListFromExercises = (exercisesResponse) => {
  const gameList = []
  const userCache = {}

  if (!exercisesResponse?.exercises) {
    return { gameList, userCache }
  }

  const exercises = exercisesResponse.exercises

  for (const exercise of exercises) {
    const isGroup = exercise.type === "group"

    // Build game_scenario from exercise.scenario
    const game_scenario = exercise.scenario
      ? {
          scenario_id: exercise.scenario.id,
          icon_name: [exercise.scenario.icon],
          scenario_name: exercise.scenario.name,
          scenario_details: {
            messaging_type: [],
            user_type: [],
            organisation_type: [],
          },
        }
      : {
          scenario_id: "",
          icon_name: ["user-secret"],
          scenario_name: "Unknown scenario",
          scenario_details: {
            messaging_type: [],
            user_type: [],
            organisation_type: [],
          },
        }

    // Format schedule from timestamp on the client (user's timezone), not server-formatted values
    const game_schedule = gameScheduleForMillisecondsSinceEpoch(exercise.schedule?.timestamp)

    // Transform participants
    const participants = (exercise.participants || []).map((p) => ({
      user_id: p.userId,
      email: p.email,
      name: p.name,
      title: p.title,
      role: p.role,
      isBot: p.isBot === true,
    }))

    // Transform spectators
    const spectators = (exercise.spectators || []).map((s) => ({
      user_id: s.userId,
      email: s.email,
      name: s.name,
    }))

    // Transform reviewers (objects → email strings)
    const reviewers = (exercise.reviewers || []).map((r) => r.email)

    // Build user cache from participants and spectators
    for (const p of exercise.participants || []) {
      if (p.userId && !userCache[p.userId]) {
        userCache[p.userId] = { email: p.email, name: p.name, picture: null }
      }
    }
    for (const s of exercise.spectators || []) {
      if (s.userId && !userCache[s.userId]) {
        userCache[s.userId] = { email: s.email, name: s.name, picture: null }
      }
    }

    if (isGroup) {
      // Add group header (root) entry
      gameList.push({
        game_id: exercise.id,
        org_name: exercise.org?.name ?? null,
        org_id: exercise.org?.id ?? null,
        // Whether the exercise's org has KPI analytics (Compare runs / Score follow the exercise's org).
        org_kpi: exercise.org?.kpiAnalytics,
        game_scenario,
        game_schedule,
        participants,
        spectators,
        reviewers,
        status: gameStatusFor(exercise.status),
        isGroupRoot: true,
        isGroupNode: false,
        groupId: exercise.id,
        archived: exercise.archived ?? false,
        ended: exercise.ended ?? null,
        // KPI P2 §10: list Score (a group's rounded member mean; absent for players).
        score: exercise.score ?? null,
        guidanceMode: exercise.guidanceMode ?? null,
        // collaborative | independentGroup | solo — the dashboard's "How exercises are run" split.
        setupType: exercise.setupType ?? null,
        // Live (host-paced) CTF — the row offers the host console / presenter view.
        ctfLive: exercise.ctfLive === true,
        // CTF reports spec §1/§6: the Type filter and badge; the server's own classification
        // (exerciseType) when it sends one, else derived from the members.
        ...exerciseTypeFields(exercise, exercise.childGames),
        ctfGroupTop: ctfGroupTop(exercise.childGames),
      })

      // Add child games as group members
      for (const child of exercise.childGames || []) {
        const childParticipants = (child.participants || []).map((p) => ({
          user_id: p.userId,
          email: p.email,
          name: p.name,
          title: p.title,
          role: p.role,
          // Preserve the AI-bot flag so a group member's AI stakeholder renders with the
          // "(AI)" badge + role in UserProfiles (mirrors the top-level participants map).
          isBot: p.isBot === true,
        }))

        // Build user cache from child participants
        for (const p of child.participants || []) {
          if (p.userId && !userCache[p.userId]) {
            userCache[p.userId] = { email: p.email, name: p.name, picture: null }
          }
        }

        gameList.push({
          game_id: child.id,
          org_name: exercise.org?.name ?? null,
          org_id: exercise.org?.id ?? null,
          // Whether the exercise's org has KPI analytics (Compare runs / Score follow the exercise's org).
          org_kpi: exercise.org?.kpiAnalytics,
          game_scenario,
          // Show each member's own start time (dispatched, else scheduled),
          // falling back to the group's time — so members added later (which
          // start after the original batch) show their actual start.
          game_schedule: gameScheduleForMillisecondsSinceEpoch(child.dispatchTime ?? child.scheduledTime ?? exercise.schedule?.timestamp),
          participants: childParticipants,
          spectators: [],
          reviewers: [],
          status: gameStatusFor(child.status),
          dispatchError: child.dispatchError ?? null,
          inviteFailures: child.inviteFailures ?? null,
          isGroupRoot: false,
          isGroupNode: true,
          groupId: exercise.id,
          archived: exercise.archived ?? false,
          ended: child.ended ?? null,
          ...exerciseTypeFields({ ...child, guidanceMode: child.guidanceMode ?? exercise.guidanceMode }),
        })
      }
    } else {
      // Individual game
      gameList.push({
        game_id: exercise.id,
        org_name: exercise.org?.name ?? null,
        org_id: exercise.org?.id ?? null,
        // Whether the exercise's org has KPI analytics (Compare runs / Score follow the exercise's org).
        org_kpi: exercise.org?.kpiAnalytics,
        game_scenario,
        game_schedule,
        participants,
        spectators,
        reviewers,
        status: gameStatusFor(exercise.status),
        dispatchError: exercise.dispatchError ?? null,
        inviteFailures: exercise.inviteFailures ?? null,
        isGroupRoot: false,
        isGroupNode: false,
        groupId: null,
        archived: exercise.archived ?? false,
        ended: exercise.ended ?? null,
        score: exercise.score ?? null,
        guidanceMode: exercise.guidanceMode ?? null,
        // collaborative | independentGroup | solo — the dashboard's "How exercises are run" split.
        setupType: exercise.setupType ?? null,
        // Live (host-paced) CTF — the row offers the host console / presenter view.
        ctfLive: exercise.ctfLive === true,
        ...exerciseTypeFields(exercise),
      })
    }
  }

  return { gameList, userCache }
}

const gameListEntryFromSession = ({ session, scenarios, sessionGroupId = null, orgs = null }) => {
  // Locate the scenario or this session
  const sessionScenarioId = session?.test
  let thisScenario = null
  if (sessionScenarioId) {
    thisScenario = scenarios?.find((scenario) => scenario.name === sessionScenarioId) ?? null
  }
  // Resolve the org info
  let org_name = session?.org ?? ""
  if (session?.org && orgs?.[session.org]) {
    org_name = orgs?.[session.org]?.displayName ?? session.org
  }
  return {
    game_id: session?.name ?? null,
    org_name,
    org_id: session?.org ?? null,
    game_scenario: thisScenario
      ? gameScenarioListFromData(thisScenario)
      : {
          scenario_id: sessionScenarioId ?? "",
          icon_name: ["user-secret"],
          scenario_name: sessionScenarioId ?? "Unknown scenario",
          scenario_details: {
            messaging_type: [],
            user_type: sessionRoles(session),
            organisation_type: [],
          },
        },
    game_schedule: gameScheduleForSession(session),
    participants: sessionParticipantIdsAndEmails(session),
    spectators: sessionSpectatorIds(session),
    reviewers: sessionReviewerEmails(session),
    status: gameStatusFor(session?.status),
    isGroupRoot: false, // This is a 'group game' object
    isGroupNode: sessionGroupId !== null, // This is a member of a 'group game'
    groupId: sessionGroupId,
    archived: session?.archived ?? false,
    ended: session?.ended ?? null,
  }
}

const gameListEntriesFromSessionGroup = ({ sessionGroup, scenarios, orgs }) => {
  const sessionGroupId = sessionGroup?.name ?? null
  let sessionGroupOrgName = null
  let sessionGroupOrgId = null
  let sessionGroupScenario = null
  const uniqueSessionParticipantIds = new Set()
  const uniqueSessionSpectatorIds = new Set()
  const sessionGroupParticipants = []
  const sessionGroupSpectators = []

  // Group is 'archived' iff all member sessions are archived
  let allSessionsAreArchived = true

  // Track if any member session have been 'ended'
  let anySessionsEnded = null

  const entries = []
  if (sessionGroup?.sessions) {
    // extract member sessions from the list
    sessionGroup.sessions.forEach((session) => {
      // Map member sessions to game list entries
      const thisGameListEntry = gameListEntryFromSession({
        session,
        scenarios,
        sessionGroupId,
        orgs,
      })
      entries.push(thisGameListEntry)
      // Check for org info
      if (!sessionGroupOrgName && thisGameListEntry.org_name) {
        sessionGroupOrgName = thisGameListEntry.org_name
      }
      if (!sessionGroupOrgId && thisGameListEntry.org_id) {
        sessionGroupOrgId = thisGameListEntry.org_id
      }
      // Check for scenario info
      if (!sessionGroupScenario && thisGameListEntry.game_scenario) {
        sessionGroupScenario = thisGameListEntry.game_scenario
      }
      // Determine group 'archived' status
      if (thisGameListEntry.archived !== true) {
        allSessionsAreArchived = false
      }
      // Check 'ended status
      if (thisGameListEntry.ended === true) {
        anySessionsEnded = true
      }

      // Aggregate participants, spectators
      if (thisGameListEntry) {
        for (const aParticipant of thisGameListEntry.participants) {
          if (uniqueSessionParticipantIds.has(aParticipant.user_id) === false) {
            sessionGroupParticipants.push(aParticipant)
            uniqueSessionParticipantIds.add(aParticipant.user_id)
          }
        }
        for (const aSpectator of thisGameListEntry.spectators) {
          if (uniqueSessionSpectatorIds.has(aSpectator.user_id) === false) {
            sessionGroupSpectators.push(aSpectator)
            uniqueSessionSpectatorIds.add(aSpectator.user_id)
          }
        }
      }
    })
  }

  if (entries.length > 0 && sessionGroupId) {
    // Only include the group header entry if there are member sessions
    entries.unshift({
      game_id: sessionGroupId,
      org_name: sessionGroupOrgName,
      org_id: sessionGroupOrgId,
      game_scenario: sessionGroupScenario,
      game_schedule: gameScheduleForSessionGroup(sessionGroup),
      participants: sessionGroupParticipants,
      spectators: sessionGroupSpectators,
      status: gameStatusFor(sessionGroup?.status),
      isGroupRoot: true, // This is a 'group game' object
      isGroupNode: false, // This is a member of a 'group game'
      groupId: sessionGroupId,
      archived: allSessionsAreArchived,
      ended: anySessionsEnded,
    })
  }
  return entries
}

export const latestSystemPromptFromSession = (session) => {
  // Get the last incident detail object that meets these criteria:
  // 1. from author 'player'
  // 2. diag == false
  // 3. type == 'text'
  const detailsFromSystem =
    session?.incidentDetails?.filter(({ author, type, diag }) => author === "player" && type === "text" && diag !== true) ?? []
  const lastPlayerIncidentDetail = detailsFromSystem?.slice(-1)?.[0]
  return lastPlayerIncidentDetail?.data?.text ?? null
}

export const playboardURL = (id) => {
  return id === null ? ROUTE_PATHS.GAME_PLAYBOARD : ROUTE_PATHS.GAME_ROOT + `/${encodeRFC3986URIComponent(id)}`
}

export const facilitatorViewURL = (id) => {
  return CLIENT_BASE_URL + ROUTE_PATHS.GAME_ROOT + `/${encodeRFC3986URIComponent(id)}/facilitator`
}

// Open the facilitator view in a new tab that KEEPS the sign-in. Both sign-ins live in per-tab
// sessionStorage — the player email-code session (playerSessionStore) and, on Keycloak, the OIDC user
// (oidc-client-ts default) — and a browser copies sessionStorage into a new tab only when it has an
// opener. "noopener" (used here before) gave the tab empty storage, so it bounced to the login page and a
// code-signed-in facilitator could not get in. The URL is our own origin, so keeping the opener is safe.
export const openFacilitatorView = (id) => window.open(facilitatorViewURL(id), "_blank")

// Multi-facilitator: the lowercased, de-duplicated set of a session's facilitator emails,
// unioning the co-equal `facilitators` array with the legacy single `facilitator` binding.
// Single source of truth for "is <email> a facilitator of this session?" membership checks.
export const sessionFacilitatorEmailSet = (session) =>
  new Set([...(session?.facilitators ?? []).map((f) => f?.email?.toLowerCase()), session?.facilitator?.email?.toLowerCase()].filter(Boolean))

// Convenience membership predicate for a given email.
export const isSessionFacilitator = (session, email) => email != null && sessionFacilitatorEmailSet(session).has(String(email).toLowerCase())

export const dashboardURL = (id) => {
  return id === null ? ROUTE_PATHS.DASHBOARD : ROUTE_PATHS.DASHBOARD + `/${encodeRFC3986URIComponent(id)}`
}

export const actionsListURL = ({ id, trendId = null }) => {
  // Build the full url (w/ trendId if needed)
  if (id && trendId) {
    return (
      ROUTE_PATHS.DASHBOARD +
      `/${encodeRFC3986URIComponent(id)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.ACTIONS_LIST_WITH_TRENDS}/${encodeRFC3986URIComponent(
        trendId
      )}`
    )
  } else if (id) {
    return ROUTE_PATHS.DASHBOARD + `/${encodeRFC3986URIComponent(id)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.ACTIONS_LIST}`
  }

  return ROUTE_PATHS.DASHBOARD + `/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.ACTIONS_LIST}`
}

export const actionsDetailURL = ({ id, actionItemId }) => {
  if (id && actionItemId) {
    return (
      ROUTE_PATHS.DASHBOARD +
      `/${encodeRFC3986URIComponent(id)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.RECOMMENDATION_ACTION_ROOT}/${encodeRFC3986URIComponent(
        actionItemId
      )}`
    )
  }

  return null
}

export const recommendationsURL = (id) => {
  return id === null
    ? ROUTE_PATHS.DASHBOARD + `/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.RECOMMENDATIONS}`
    : ROUTE_PATHS.DASHBOARD + `/${encodeRFC3986URIComponent(id)}/${ROUTE_PATHS.DETAILS.ROOT}/${ROUTE_PATHS.DETAILS.RECOMMENDATIONS}`
}

export const reportURL = (id) => {
  return id === null ? ROUTE_PATHS.REPORT : ROUTE_PATHS.REPORT + `/${encodeRFC3986URIComponent(id)}`
}

// CTF reports spec §2/§3: a CTF session's report and a CTF group's staff event report.
export const ctfReportURL = (id) => ROUTE_PATHS.CTF_REPORT + `/${encodeRFC3986URIComponent(id)}`
export const ctfEventReportURL = (groupName, questionId = null) =>
  ROUTE_PATHS.CTF_EVENT_REPORT + `/${encodeRFC3986URIComponent(groupName)}` + (questionId ? `/q/${encodeRFC3986URIComponent(questionId)}` : "")

export const editGameURL = (id) => {
  return id === null ? ROUTE_PATHS.GAME_EDIT : ROUTE_PATHS.GAME_EDIT + `/${encodeRFC3986URIComponent(id)}`
}

export const restartGameURL = (id) => {
  return id === null ? ROUTE_PATHS.GAME_RESTART : ROUTE_PATHS.GAME_RESTART + `/${encodeRFC3986URIComponent(id)}`
}

export const duplicateGameURL = (id) => {
  return id === null ? ROUTE_PATHS.GAME_DUPLICATE : ROUTE_PATHS.GAME_DUPLICATE + `/${encodeRFC3986URIComponent(id)}`
}

// Whether the WHOLE exercise is over (not just one player's part). Multiplayer sessions rely on
// session.ended (see viewModeForUserInSession); individual sessions on the finished status.
export const sessionIsOver = (session) => {
  if (!session) return false
  const isMultiplayerSession = Array.isArray(session?.players) && session.players.length > 0
  return isMultiplayerSession ? session?.ended === true : gameStatusFor(session?.status) === TABLE_GAME_STATUS_FILTER.FINISHED
}

export const viewModeForUserInSession = ({ user, session }) => {
  const email = userEmail(user)
  if (!email || !session) {
    return GAME_MODES.VIEW_ONLY
  }

  const emailLower = email.toLowerCase()

  // For multiplayer sessions (players[] is non-empty), rely on session.ended rather than
  // session.status. The computed status field can reach FINISHED when the scenario state
  // machine runs out of transitions for one player, even though other players are still
  // playing and session.ended is still false. session.ended is only set to true when ALL
  // players have completed (via setParticipantCompleted / markParticipantCompleted) or a sponsor/manager force-finishes.
  // For individual sessions (players[] is empty/absent), keep the original status check.
  const isMultiplayerSession = Array.isArray(session?.players) && session.players.length > 0

  if (sessionIsOver(session)) {
    return GAME_MODES.GAME_OVER
  }

  // For multiplayer: if this specific player has completed their own turn,
  // show Game Over for them even while other players are still playing.
  if (isMultiplayerSession) {
    const thisPlayerEntry = session.players.find((p) => p.email?.toLowerCase() === emailLower)
    if (thisPlayerEntry?.completed === true) {
      return GAME_MODES.GAME_OVER
    }
  }

  if (session.currentPlayerEmail?.toLowerCase() === emailLower) {
    return GAME_MODES.GAME_PLAY
  }

  // Check if user is one of the current players (main multi-player support)
  const currentPlayers = session?.currentPlayers ?? []
  const userIsCurrentPlayer = currentPlayers.some((player) => player.email?.toLowerCase() === emailLower)
  if (userIsCurrentPlayer) {
    return GAME_MODES.GAME_PLAY
  }

  // Check if user is a spectator with canPlay enabled (existing multiplayer mode)
  const userCanPlay = session?.spectators?.some((spec) => spec.email?.toLowerCase() === emailLower && spec.canPlay === true)
  if (userCanPlay) {
    return GAME_MODES.GAME_PLAY
  }

  return GAME_MODES.VIEW_ONLY
}

export const playingUserWaitingMessage = () => {
  return {
    type: "text",
    author: "me",
    id: null,
    data: { text: "..." },
    suggestions: [],
  }
}

export const LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE = Object.freeze({
  REQUIRED: "This field is required.",
  EMAIL: "Email is not valid.",
  NUMBER_MIN: "Number is below the minimum value.",
  NUMBER_MAX: "Number is above the maxiumum value.",
  MAX_LENGTH: "Value must be $$max_value characters or less.",
  MIN_LENGTH: "Value must be at least $$min_value characters.",
  FUTURE_DATETIME: "Date must be in the future.",
  SAFETEXT: validTextErrorMessage,
  SAFENAME: validNameErrorMessage,
  SAFE_API_NODE_NAME: validAPINodeErrorMessage,
  VALID_PLAYER_LIST_CSV: "File contains invalid player names and/or emails.",
})

const errorMessageReplacer = (validationType, errorMessage, value) => {
  switch (validationType) {
    case VALIDATION_TYPES.MAX_LENGTH: {
      return String(errorMessage).replace(`$$max_value`, value)
    }
    case VALIDATION_TYPES.MIN_LENGTH: {
      return String(errorMessage).replace(`$$min_value`, value)
    }
    default:
      return errorMessage
  }
}

export const paramNameForRole = ({ role, param }) => {
  return role.toLowerCase().split(" ").join("_") + "_" + (param ?? "")
}

// The explicit "run the whole incident" choice offered in the free-text Role field (a datalist
// suggestion). Selecting it — or leaving the field blank — scopes to every role.
export const ALL_ROLES_LABEL = "All roles"

// Normalize a Role field value (the free-text ROLE_INPUT string, or a legacy MULTI_DROPDOWN array)
// into the soloRoles wire shape (string[]). Blank or the "All roles" sentinel → [] (whole incident).
// The TYPED value(s) are sent through verbatim — the SERVER resolves each to a scenario role (exact →
// normalized → synonym → embedding), so the client never has to match the fixed list. Deduped.
//
// A string value is comma-split: a role name never contains a comma (the same invariant the [ROLE]
// tags rely on), so a comma is a safe separator. This is a LOSSLESS carrier for a legacy session that
// stored several soloRoles on one row (seeded back as "A, B") — re-saving it untouched preserves both
// rather than silently dropping to the first. The single-role model still holds: typical use is one
// role per field; the picker offers one at a time.
export const soloRolesFromRoleFieldValue = (value) => {
  const parts = Array.isArray(value) ? value : value === null || value === undefined || value === "" ? [] : String(value).split(",")
  const cleaned = parts.map((v) => String(v ?? "").trim()).filter((v) => v && v.toLowerCase() !== ALL_ROLES_LABEL.toLowerCase())
  return [...new Set(cleaned)]
}

// The Role field's seed value from a session's stored soloRoles (string[]). [] → "" (blank = All roles);
// one role → that role; several (legacy) → "A, B" so an untouched re-save preserves every role.
export const roleFieldValueFromSoloRoles = (soloRoles) => {
  const list = (Array.isArray(soloRoles) ? soloRoles : []).map((r) => String(r ?? "").trim()).filter(Boolean)
  return [...new Set(list)].join(", ")
}

// One blank roster row seeded from a ROSTER group's field defaults, so the
// bespoke Players / Observers tables always show a persistent editable row (no
// "No players yet" empty state). Field NAMES / defaults are taken straight from
// the group's `fields` so the row shape matches what the launch payload expects.
const seedRosterRow = (fields) => {
  const row = {}
  ;(fields ?? []).forEach((f) => {
    row[f.name] = f.type === FIELD_TYPES.FACILITATOR_TOGGLE ? false : (f.value ?? (f.toggleValues ? f.toggleValues.off : ""))
  })
  // Stable row id so the bespoke roster's per-row toggles / remove target the
  // right row (mutual-exclusion facilitator logic keys off this).
  row[tableGroupValueIdKey] = uuidV4()
  return row
}

// Ensure a roster group value always has at least one row (the seed) so the
// bespoke table renders a persistent editable row. Existing rows (edit /
// duplicate) are kept as-is.
const rosterValueWithSeed = (existing, fields) => {
  return Array.isArray(existing) && existing.length > 0 ? existing : [seedRosterRow(fields)]
}

// EDIT round-trip: the Collaborative roster (_multiplayerPlayers) is a form field, NOT a persisted
// session field — so on edit it comes back empty. Rebuild the rows from the saved session.players[]
// (each carries name/email/role/language), flagging rows whose email is in session.facilitators.
// Returns null when there is nothing to rebuild (caller falls back to the default blank row).
const multiplayerRosterFromSession = ({ session }) => {
  const players = Array.isArray(session?.players) ? session.players.filter((p) => p?.virtual !== true) : []
  if (players.length === 0) return null
  const facilitatorEmails = new Set(
    (session?.facilitators ?? [])
      .map((f) =>
        String((f?.email ?? f) || "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  )
  return players.map((p) => ({
    name: p?.name ?? "",
    email: p?.email ?? "",
    // Role is a free-text STRING (ROLE_INPUT). Seed from every role the person typed (typedRoles —
    // several roles are stored with role: null), else their stored scope: soloRoles if present, else
    // the scalar role; "" = All roles; several → "A, B" (lossless comma carrier).
    role: roleFieldValueFromSoloRoles(
      Array.isArray(p?.typedRoles) ? p.typedRoles : Array.isArray(p?.soloRoles) && p.soloRoles.length ? p.soloRoles : p?.role ? [p.role] : []
    ),
    title: p?.title ?? "",
    language: p?.language ?? "",
    isFacilitator: facilitatorEmails.has(
      String(p?.email ?? "")
        .trim()
        .toLowerCase()
    ),
  }))
}

// EDIT round-trip: the Independent-Group roster (_groupPlayers) is also not persisted. Rebuild it from
// the group's MEMBER sessions (session.sessions) — each member is a solo session carrying its own
// player (roleAssignments/roleAssigneeNames or players[0]), per-member soloRoles (their picked seats;
// [] = All roles, a MULTI_DROPDOWN of seats) and language. Returns null when there are no members.
const groupRosterFromSession = ({ session }) => {
  const members = Array.isArray(session?.sessions) ? session.sessions : null
  if (!members || members.length === 0) return null
  return members.map((m) => {
    const anchorRole = Object.keys(m?.roleAssignments ?? {})[0] ?? null
    const email = memberPrimaryEmail(m)
    const name = (anchorRole ? m?.roleAssigneeNames?.[anchorRole] : null) ?? m?.players?.[0]?.name ?? ""
    const language = m?.defaultLanguage ?? (anchorRole ? m?.roleAssigneeLanguages?.[anchorRole] : null) ?? m?.players?.[0]?.language ?? ""
    return {
      name,
      email,
      // Free-text Role field (ROLE_INPUT) is a STRING: the member's sole scoped role, or "" for All
      // roles (soloRoles []). A legacy multi-seat member keeps its first role (multi-select is retired).
      role: roleFieldValueFromSoloRoles(m?.soloRoles), // per-member scope; "" = All roles; several (legacy) → "A, B"
      title: m?.players?.[0]?.title ?? "",
      language,
    }
  })
}

const layoutFieldValidationsForVariable = (theVariable) => {
  let layoutFieldValidations = []
  const isPrivateVar = gameScenarioVariableIsPrivate(theVariable)
  switch (theVariable.type) {
    case "STRING": {
      layoutFieldValidations = []
      // Setup character set validation rule
      if (isPrivateVar) {
        // We don't need character validation for private variables
      } else {
        layoutFieldValidations.push({
          type: VALIDATION_TYPES.SAFETEXT,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFETEXT,
        })
      }
      // Setup field length validation rule
      let maxLengthChars = asNumber(getReplaneConfig("frontend", "validation.max_text_entry_length", MAX_INPUT_LIMIT), MAX_INPUT_LIMIT)
      if (isPrivateVar) {
        maxLengthChars = asNumber(
          getReplaneConfig("frontend", "validation.max_private_text_entry_length", MAX_PRIVATE_INPUT_LIMIT),
          MAX_PRIVATE_INPUT_LIMIT
        )
      }
      layoutFieldValidations.push({
        type: VALIDATION_TYPES.MAX_LENGTH,
        value: maxLengthChars,
        error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, maxLengthChars),
      })
      break
    }
    case "ENUM":
      break
    case "MULTI_ENUM_WITH_STRING_OPTION":
      break
    case "ENUM_WITH_STRING_OPTION":
      break
    default:
      break
  }
  // Check variable default
  if (theVariable.default === null) {
    layoutFieldValidations.push({
      type: VALIDATION_TYPES.REQUIRED,
      error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
    })
  }
  return layoutFieldValidations
}

const layoutFieldTypeForVariableType = (varType) => {
  let layoutFieldType = FIELD_TYPES.STRING
  switch (varType) {
    case "STRING":
      layoutFieldType = FIELD_TYPES.STRING
      break
    case "ENUM":
      layoutFieldType = FIELD_TYPES.OPTIONS
      break
    case "MULTI_ENUM_WITH_STRING_OPTION":
      layoutFieldType = FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER
      break
    case "ENUM_WITH_STRING_OPTION":
      layoutFieldType = FIELD_TYPES.OPTIONS_WITH_STRING_OTHER
      break
    default:
      break
  }
  return layoutFieldType
}

const layoutFieldValueForVariable = ({ variable, rawVal }) => {
  const fieldType = layoutFieldTypeForVariableType(variable.type)
  let layoutFieldValue
  switch (fieldType) {
    case FIELD_TYPES.STRING:
      layoutFieldValue = rawVal || ""
      break
    case FIELD_TYPES.OPTIONS:
      layoutFieldValue = rawVal || variable?.default || ""
      break
    case FIELD_TYPES.MULTI_OPTIONS_WITH_STRING_OTHER:
      if (!rawVal && variable?.default) {
        layoutFieldValue = [variable.default.trim()]
      } else {
        layoutFieldValue = listFromCommaDelimitedString(rawVal)
      }
      break
    case FIELD_TYPES.OPTIONS_WITH_STRING_OTHER:
      layoutFieldValue = rawVal || variable?.default || ""
      break
    default:
      break
  }
  return layoutFieldValue
}

const layoutFieldPlaceholderForVariable = ({ variable }) => {
  const fieldType = layoutFieldTypeForVariableType(variable.type)
  let layoutFieldPlaceholder = ""
  switch (fieldType) {
    case FIELD_TYPES.STRING:
      layoutFieldPlaceholder = variable?.default || ""
      break
    default:
      break
  }
  return layoutFieldPlaceholder
}

const fieldOptionsForScenarioVariable = (scenarioVar) => {
  const opts = []
  scenarioVar?.enums?.forEach((enumItem) => {
    opts.push({
      value: enumItem,
      display: enumItem,
    })
  })
  return opts
}

const listFromCommaDelimitedString = (commaDelimitedString) => {
  return (
    commaDelimitedString
      ?.trim()
      .split(",")
      .map((role) => role.trim())
      .filter((role) => role !== "") ?? []
  )
}

// Facilitated-roundtable virtual seats. MUST mirror the server's roleSlug /
// virtualSeatEmail (server/utils/facilitatedRoundtableUtils.js): reserved domain,
// lowercase slug, non-alphanumerics collapsed to '-'.
export const VIRTUAL_SEAT_DOMAIN = "seat.virtual"
const roleSlugForSeat = (role) =>
  String(role ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "seat"
export const virtualSeatEmail = (role) => `${roleSlugForSeat(role)}@${VIRTUAL_SEAT_DOMAIN}`

// True for a roundtable seat id ("<slug>@seat.virtual" or "virtual:<slug>"). Seats have no inbox, so
// they are never @mention / private-message targets.
export const isVirtualSeatId = (value) => {
  const lower = String(value ?? "")
    .trim()
    .toLowerCase()
  return lower.startsWith("virtual:") || lower.endsWith(`@${VIRTUAL_SEAT_DOMAIN}`)
}

// A decision the facilitator recorded for a seat is authored "virtual:<slug>"; the seat's email is
// "<slug>@seat.virtual". Neither may reach a (shared) screen. Resolves either to the named participant
// on that seat, else the seat's role; else a title-cased slug. null when `value` is not a seat id.
export const virtualSeatDisplayName = ({ value, players = [] }) => {
  const raw = String(value ?? "").trim()
  const lower = raw.toLowerCase()
  let slug = null
  if (lower.startsWith("virtual:")) slug = lower.slice("virtual:".length)
  else if (lower.endsWith(`@${VIRTUAL_SEAT_DOMAIN}`)) slug = lower.slice(0, -(VIRTUAL_SEAT_DOMAIN.length + 1))
  if (slug === null) return null
  const seat = (players ?? []).find((p) => p?.virtual === true && p?.role && roleSlugForSeat(p.role) === slug)
  if (seat) return String(seat.name ?? "").trim() || String(seat.role).trim()
  return (
    slug
      .split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ") || "Seat"
  )
}

export const rolesDefinedByScenario = (scenario) => {
  // `scenario.roles` is the server's authoritative role list — the DISTINCT set of turn
  // "Assigned To" values (chaosScenarioUtils `roles: [...allRoleNames]`). A genuine multi-role
  // scenario is already ["IT ADMIN","SOC LEAD"]; a single role whose NAME contains a comma
  // (e.g. "IT ADMIN, SOC LEAD" typed into the generator's one role field) is ONE entry.
  // Do NOT comma-split — that would wrongly turn a single comma-named role into two, so the
  // assignments wouldn't match any turn's owner and no one could play. Just normalize.
  const raw = scenario?.roles ?? []
  const list = Array.isArray(raw) ? raw : [raw]
  const seen = new Set()
  return list.map((r) => String(r ?? "").trim()).filter((r) => r && !seen.has(r) && seen.add(r))
}

// Roles offered as type-ahead SUGGESTIONS. Deliberately wider than rolesDefinedByScenario (which
// stays the authoritative scoping list): a scenario's Info "Roles" can name roles that no action
// carries a [ROLE] tag for — the detail page shows all four for eDiscovery while only one owns
// actions. Those roles are still perfectly usable (the server resolves an unmatched role to the
// whole incident, so the player gets every action), they just were not being offered. Suggestions
// are a convenience list, so widening them cannot mis-scope anything.
// Participants who can actually be sent an exercise invitation.
//
// The roster mixes real people with AI participants ("AI stakeholders" / react bots).
// A bot has no account and no mailbox, so an invitation for one is meaningless — it
// rendered as "Send invitation · null" in the exercise kebab menu and, if clicked,
// invited nobody. A human row can also carry a blank email and behaves the same way.
export const invitableParticipants = (participants) =>
  (Array.isArray(participants) ? participants : []).filter(
    (participant) => participant?.isBot !== true && String(participant?.email ?? "").trim() !== ""
  )

// Wizard flows that must open on the SOURCE exercise's run format.
//
// setupType (Solo / Collaborative / Independent Group / Facilitated) is NOT persisted on the
// session, so it has to be re-derived from the saved data. Any flow that starts from an existing
// exercise and omits this falls through to the form's "group" default, which renders an empty
// multi-role roster — the participants silently disappear. That was the Duplicate bug: it opened
// with the wrong format and no players instead of the source exercise's setup.
export const restoresSavedSetupType = (userAction) =>
  userAction === TABLE_GAME_SCHEDULE_ACTION.EDIT ||
  userAction === TABLE_GAME_SCHEDULE_ACTION.RESTART ||
  userAction === TABLE_GAME_SCHEDULE_ACTION.DUPLICATE

export const roleSuggestionsForScenario = (scenario) => {
  const derived = rolesDefinedByScenario(scenario)
  const declared = String(scenario?.info?.["Roles"] ?? "")
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean)
  const seen = new Set()
  return [...derived, ...declared].filter((r) => r && !seen.has(r.toLowerCase()) && seen.add(r.toLowerCase()))
}

// Facilitated-roundtable SEATS: every role the scenario names — the action owners
// (rolesDefinedByScenario) first, then the Info "Roles" roster. A role that owns no actions still
// gets a seat so its named participant can be seated and recorded: the roundtable is whole-incident,
// so that seat's decisions rank against every action (the same "sees all actions" rule the scenario
// page states). The roster is only widened when it reads as clean role titles (the builder's
// _writer_safe_role shape: <= 60 chars, <= 8 words, balanced parentheses) — a legacy prose roster
// ("one for each region: U.S., Lithuania, and India)") comma-splits into fragments that must never
// become seats, so it falls back to the action owners exactly as before.
const isCleanSeatRole = (role) => {
  const r = String(role ?? "").trim()
  if (r.length < 2 || r.length > 60 || r.split(/\s+/).length > 8) return false
  let depth = 0
  for (const ch of r) {
    if (ch === "(") depth += 1
    else if (ch === ")") depth -= 1
    if (depth < 0) return false
  }
  return depth === 0
}
export const facilitatedSeatRolesForScenario = (scenario) => {
  const derived = rolesDefinedByScenario(scenario)
  const declared = String(scenario?.info?.["Roles"] ?? "")
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean)
  if (declared.length === 0 || !declared.every(isCleanSeatRole)) return derived
  return roleSuggestionsForScenario(scenario)
}

const orgAccessForScenario = (scenario) => {
  return scenario?.orgAccess ?? null
}

const defaultGameName = ({ duplicatingSessionId = null, scenario } = {}) => {
  return duplicatingSessionId ? `copy-of-${duplicatingSessionId}` : `${scenario?.info?.Name ?? scenario?.name ?? "new-game"}`
}

const defaultGroupGameName = ({ scenario } = {}) => {
  return `${scenario?.info?.Name ?? scenario?.name ?? "new-group"}`
}

const GROUP_GAME_FORM_FIELDS = Object.freeze({
  PLAYER_LIST: "_groupPlayers",
  BATCH_SIZE: "_groupBatchSize",
  BATCH_START_INTERVAL_MINUTES: "_groupBatchStartIntervalMinutes",
})

export const GAME_SETUP_TYPE_FORM_FIELDS = Object.freeze({
  SETUP_GROUP_BOOLEAN: "_isGroupGameSetup",
  NLP_AUTO_DISABLE_THRESHOLD: "_nlpAutoDisableThreshold",
  NLP_MODE_BOOLEAN: "_nlpModeBoolean",
  NLP_MODEL_PROMPT_MATCHED_ACTIONS_PRESCREEN: "_nlpModelPromptMatchedActionsPrescreen",
  // Solo doubles as the lightweight way to FACILITATE for a room (one person drives / screen-shares).
  // "Yes" reveals a facilitator name/email + a +Add "Additional facilitators" roster; the captured
  // people flow into session.facilitators (carried into details_form at submit).
  SOLO_IS_FACILITATOR: "_soloIsFacilitator", // "Yes" | "No"
})

export const MULTIPLAYER_FORM_FIELDS = Object.freeze({
  PLAYER_LIST: "_multiplayerPlayers",
  CHAT_ENABLED: "_chatEnabled",
  SHOW_OTHER_PLAYERS_ACTIONS: "_showOtherPlayersActions",
  GUIDANCE_MODE: "_guidanceMode",
  // Exercise-level default language. Seeds each roster row's per-player Language
  // column; individually overridable. Canonical gameplay stays English — this only
  // drives the participant's translated display (server translation layer).
  DEFAULT_LANGUAGE: "_defaultLanguage",
})

// Supported participant languages. Mirrors the server allowlist
// (server/utils/language-utils.js): en is canonical/default, fr + fi translate.
// Unknown/absent codes normalize to en server-side.
export const SUPPORTED_LANGUAGES = Object.freeze(["en", "fr", "fi"])
export const DEFAULT_LANGUAGE = "en"
// Dropdown options for the EXERCISE-level Language select (a concrete default —
// no "inherit" choice, so en/fr/fi only).
export const LANGUAGE_OPTIONS = Object.freeze([
  { value: "en", display: "English" },
  { value: "fr", display: "Français" },
  { value: "fi", display: "Suomi" },
])
// Dropdown options for the per-ROW (roster / per-role) Language select. The leading
// "" option means "use the exercise default": the row is left unset and resolves to
// the session default at submit (normalizeLanguage(row.language || defaultLanguage)).
// Only a non-empty row value is sent explicitly on players[].language.
// Per-row Language options are the concrete languages (no abstract "default" the user
// can't interpret). A row is SEEDED to the exercise default at build (English by default)
// and shows that language; changing a row overrides it for that person.
export const ROW_LANGUAGE_OPTIONS = LANGUAGE_OPTIONS
// Coerce any value to a supported code (defensive parity with the server; the
// launch payload carries the raw string and the server re-normalizes).
const normalizeLanguage = (value) => {
  const code = typeof value === "string" ? value.trim().toLowerCase() : ""
  return SUPPORTED_LANGUAGES.includes(code) ? code : DEFAULT_LANGUAGE
}

// Seed a per-role row's Language select on edit/duplicate. Returns a supported code
// only when the session actually persisted one for this role, else "" (the row inherits
// the exercise default). Priority: explicit roleAssigneeLanguages[role] → the player[]
// entry's language for this role's email → "". Never falls back to the exercise default
// here — that inheritance happens at submit, so a changed default re-seeds these rows.
const seededRoleLanguage = ({ session, roleName }) => {
  const explicit = session?.roleAssigneeLanguages?.[roleName]
  if (typeof explicit === "string" && SUPPORTED_LANGUAGES.includes(explicit.trim().toLowerCase())) {
    return explicit.trim().toLowerCase()
  }
  const roleEmail = session?.roleAssignments?.[roleName]
  if (roleEmail && Array.isArray(session?.players)) {
    const playerEntry = session.players.find((p) => p?.email?.toLowerCase() === String(roleEmail).toLowerCase())
    const lang = playerEntry?.language
    if (typeof lang === "string" && SUPPORTED_LANGUAGES.includes(lang.trim().toLowerCase())) {
      return lang.trim().toLowerCase()
    }
  }
  // No explicit per-role language → show the exercise default concretely (English by default).
  return normalizeLanguage(session?.defaultLanguage)
}

// Live propagation: when the exercise-level Language changes, every roster row that still
// follows the OLD default should move to the NEW default, while rows the user explicitly set
// to a different language are preserved. Pure + immutable so it can drive a form value-update
// effect and be unit-tested directly.
//
// A row "follows the default" when its language equals prevDefault OR is blank/absent (an
// unseeded row inherits the default at submit). Handles all roster shapes in the details
// form: array rosters (_multiplayerPlayers, _groupPlayers, spectators — each row carries a
// `language`) and the flat per-role `<role>_language` keys. `_defaultLanguage` itself is set
// to the new value. Returns a NEW values object only if something changed, else the original
// reference (so callers can skip a no-op dispatch).
export const reseedLanguagesOnDefaultChange = (values, prevDefault, newDefault) => {
  if (!values || typeof values !== "object") return values
  const prev = normalizeLanguage(prevDefault)
  const next = normalizeLanguage(newDefault)
  const follows = (lang) => {
    const code = typeof lang === "string" ? lang.trim().toLowerCase() : ""
    return code === "" || code === prev
  }
  const out = { ...values, [MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE]: next }
  let changed = values?.[MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE] !== next
  for (const [key, value] of Object.entries(values)) {
    if (key === MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE) continue
    // Array rosters: update each row's `language` when it still follows the default. The
    // roster guard is that at least one row already carries a `language`; within such an
    // array a row with no `language` yet (an untouched seat) is treated as following.
    if (Array.isArray(value) && value.some((row) => row && typeof row === "object" && "language" in row)) {
      let rowChanged = false
      const nextRows = value.map((row) => {
        if (row && typeof row === "object" && follows(row.language) && row.language !== next) {
          rowChanged = true
          return { ...row, language: next }
        }
        return row
      })
      if (rowChanged) {
        out[key] = nextRows
        changed = true
      }
      continue
    }
    // Flat per-role language keys (`<role>_language`); the exercise default is handled above.
    if (key.endsWith("_language") && typeof value === "string" && follows(value) && value !== next) {
      out[key] = next
      changed = true
    }
  }
  return changed ? out : values
}

// Solo doubles as the lightweight way to run/facilitate an exercise for a room (one person drives,
// e.g. screen-shares an in-person tabletop). A "Are you facilitating this for a room?" TOGGLE sits
// under the Solo card in the setup-type form (Card 2). When "Yes" the MAIN details form reveals a
// primary facilitator name/email + a +Add "Additional facilitators" roster (co-equal facilitators);
// nothing shows when off. Splitting toggle (setup-type form) from the value fields (main form) keeps
// the captured people in details_form, where setupParametersForScenarioFromGameDetailsValues reads
// _facilitatorEmail/_facilitatorName/_additionalFacilitators directly into session.facilitators —
// no fragile cross-form value carry.

// Toggle only — lives in the setup-type form under the Solo card. `idSeed` keeps ids unique.
const buildSoloFacilitatorToggle = ({ currentValues, idSeed }) => {
  const isFacilitating = currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SOLO_IS_FACILITATOR] === "Yes"
  return {
    id: `${idSeed}-solo-fac-toggle`,
    width: 12,
    type: FIELD_TYPES.DROPDOWN,
    label: "Add a facilitator to run this live for a room?",
    name: GAME_SETUP_TYPE_FORM_FIELDS.SOLO_IS_FACILITATOR,
    placeholder: "",
    value: isFacilitating ? "Yes" : "No",
    icon: null,
    options: [
      { value: "No", display: "No" },
      { value: "Yes", display: "Yes" },
    ],
    // Render as a single toggle (same control as the Observers "Enable chat" toggle).
    toggleValues: { on: "Yes", off: "No" },
    // The player above still plays and is scored — this just ADDS facilitator(s) who watch the live
    // board and push injects (e.g. an in-person tabletop). It does NOT change how the player is scored.
    hint: "The player above still plays and is scored. Turn this on to add facilitator(s) who watch the live board and can push injects (e.g. an in-person tabletop) — each gets their own login and Facilitator View.",
    validations: [],
    comments: "",
  }
}

// Facilitator name/email fields + the "Additional facilitators" +Add roster GROUP — rendered in the
// MAIN details form when the Solo facilitator toggle is on. Returns { fields, rosterGroup }.
const buildSoloFacilitatorFields = ({ session, idSeed }) => {
  // The primary facilitator is the single participant row itself (relabeled "Facilitator" when
  // "facilitating for a room" is on) — no separate primary name/email fields here. This builder now
  // only produces the co-facilitator roster.
  const additionalFacilitatorFields = [
    {
      id: `${idSeed}-solo-addfac-name`,
      width: 6,
      type: FIELD_TYPES.STRING,
      label: "Name",
      name: "name",
      placeholder: "Co-facilitator name",
      value: "",
      icon: null,
      options: [],
      validations: [
        { type: VALIDATION_TYPES.SAFENAME, error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME },
        {
          type: VALIDATION_TYPES.MAX_LENGTH,
          value: validNameFieldMaxLength,
          error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
        },
      ],
      comments: "",
    },
    {
      id: `${idSeed}-solo-addfac-email`,
      width: 6,
      type: FIELD_TYPES.EMAIL,
      label: "Email",
      name: "email",
      placeholder: "co-facilitator@company.com",
      value: "",
      icon: null,
      options: [],
      validations: [{ type: VALIDATION_TYPES.EMAIL, error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL }],
      comments: "",
    },
  ]
  const primaryFacilitatorEmail = (session?.facilitator?.email ?? session?.facilitators?.[0]?.email ?? "").toLowerCase()
  const additionalFacilitatorRows = (session?.facilitators ?? [])
    .filter((f) => f?.email && f.email.toLowerCase() !== primaryFacilitatorEmail)
    .map((f) => ({ name: f.name ?? "", email: f.email }))
  const rosterGroup = {
    id: `${idSeed}_solo_additional_facilitators`,
    card: "players",
    type: FIELD_GROUP_TYPES.ROSTER,
    export_enabled: false,
    import_enabled: false,
    name: "_additionalFacilitators",
    value: rosterValueWithSeed(additionalFacilitatorRows, additionalFacilitatorFields),
    label: "Additional facilitators",
    labelSuffix: "— optional",
    hint: "Co-facilitators who help run the room. Each gets their own login and full facilitator access (they're invited on launch). Use + Add for more, or leave blank for a single facilitator.",
    fields: additionalFacilitatorFields,
  }
  return { rosterGroup }
}

export const gameOrGroupCommonConfigLayout = ({ scenario, userAction, currentValues, facilitatedEnabled = false }) => {
  // A CTF scenario keeps the same format values with CTF wording (Solo / Competition / Team practice) and
  // no Facilitated Roundtable; a tabletop scenario's cards are untouched.
  const withCtfFormatWording = (field) =>
    isCtfScenario(scenario) && field?.name === GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN
      ? { ...field, options: ctfFormatOptions(field.options) }
      : field
  const scenarioRoles = rolesDefinedByScenario(scenario)
  const isSingleRoleScenario = (scenarioRoles?.length ?? 0) === 1
  const layoutGroups = []
  let view_order = 10
  let game_detail = 0
  const isLayoutForDuplicate = TABLE_GAME_SCHEDULE_ACTION.DUPLICATE === userAction

  // Only allow group game setup when creating or duplicating a game
  const allowGroupGameSetup = isSingleRoleScenario && (isLayoutForDuplicate || !userAction)

  // Multi-role scenarios: no solo/group options, but the exercise can still be run
  // COLLABORATIVELY (assign a person per role — today's default) or as a FACILITATED
  // ROUNDTABLE (virtual seats; the facilitator records the room's decisions).
  if (!isSingleRoleScenario && (isLayoutForDuplicate || !userAction)) {
    const currentSetupType = currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]
    // A multi-role scenario can now ALSO be run by one person (Solo) or by independent people
    // (Independent Group), in addition to Collaborative / Facilitated. DISTINCT values ("solo" /
    // "independentGroup") from the single-role picker's "single"/"group" so the single-role
    // default sentinel ("group") does NOT pre-select a multi-role card — multi-role still defaults
    // to Collaborative ("multiplayer").
    const multiRoleSetupValue =
      currentSetupType === "facilitated" || currentSetupType === "solo" || currentSetupType === "independentGroup" ? currentSetupType : "multiplayer"
    const typeSelectionFields = []
    typeSelectionFields.push({
      id: `game-common-detail-${++game_detail}`,
      width: 12,
      type: FIELD_TYPES.OPTION_CARDS,
      label: `How will this exercise be run?`,
      name: GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN,
      placeholder: "",
      value: multiRoleSetupValue,
      icon: null,
      options: [
        {
          value: "multiplayer",
          title: "Collaborative",
          display: "Collaborative",
          badge: "1 shared team score",
          subtitle: "a team, together, live",
          description:
            "Your team plays ONE live incident together, at the same time. Assign a real person to each role below; everyone works the same unfolding incident and the team gets one shared score.",
        },
        {
          value: "facilitated",
          title: "Facilitated Roundtable",
          display: "Facilitated Roundtable",
          badge: "1 shared team score",
          subtitle: "one facilitator runs it for a room",
          description:
            "The facilitator logs in and runs the whole exercise for a room (e.g. an in-person tabletop), recording the team's decision for each role. The room's participants need no accounts or invites of their own — only the facilitator logs in. Best for discussion sessions, and how one person can dry-run a multi-role scenario end to end.",
        },
        {
          value: "solo",
          title: "Solo",
          display: "Solo",
          badge: "1 score",
          subtitle: "just one person, or to facilitate",
          description:
            "Only you — or for facilitating an exercise for a room (share your screen and drive an in-person tabletop). Play through every role's turns yourself, or focus on ONE role. Good for solo practice, testing, or running it live for a group.",
        },
        {
          value: "independentGroup",
          title: "Independent Group",
          display: "Independent Group",
          badge: "1 score each",
          subtitle: "many people, each on their own",
          description:
            "Several people each run their OWN separate copy, on their own time — no live coordination. Each person is scored individually (compare them afterward). Each person picks their OWN role in their row of the roster below.",
        },
        // Facilitated Roundtable is gated behind the FACILITATED_ROUNDTABLE org enablement
        // (default OFF) — hidden unless the org is granted it (user direction 2026-09-10).
      ].filter((option) => option.value !== "facilitated" || facilitatedEnabled),
      validations: [],
      comments: "",
    })

    // Role selection moved OUT of this format card and INTO each person's roster row (user
    // direction 2026-09-14): the top-level "What do you play?" / "Which role?" applied ONE role to
    // everyone, which is wrong for Independent Group (each person runs their own copy and can play a
    // DIFFERENT role). Now each roster row carries its own Role selector ("All roles" or a specific
    // scenario role), which drives that person's scoring (soloRoles) — per-person for Independent
    // Group, and the single row for Solo. See the roster builder in gameSetupLayout.

    // Solo can double as facilitating for a room (one person drives) — a "facilitating for a room?"
    // toggle. Only for Solo (not Independent Group, which is many independent solo runs). When on,
    // the MAIN details form reveals the facilitator name/email + roster. The toggle stays here so it
    // sits directly under the Solo card.
    if (currentSetupType === "solo") {
      typeSelectionFields.push(buildSoloFacilitatorToggle({ currentValues, idSeed: "mr" }))
    }

    layoutGroups.push({
      view_order,
      id: "game_setup_type",
      card: "session",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: typeSelectionFields.map(withCtfFormatWording),
    })
    view_order += 10
  }

  // Game vs. Group
  if (allowGroupGameSetup) {
    const typeSelectionFields = []
    const singleRoleSetupValue =
      // "group" is what the Independent Group card WRITES (string); `true` is the legacy stored
      // boolean. Missing the string here made a selected Independent Group derive as "single",
      // which wrongly rendered the SOLO "facilitating for a room?" toggle under it.
      currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === true ||
      currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === "group"
        ? "group"
        : currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === "multiplayer"
          ? "multiplayer"
          : currentValues?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === "facilitated"
            ? "facilitated"
            : "single"
    typeSelectionFields.push({
      id: `game-common-detail-${++game_detail}`,
      width: 12,
      type: FIELD_TYPES.OPTION_CARDS,
      label: `How will this exercise be run?`,
      name: GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN,
      placeholder: "",
      value: singleRoleSetupValue,
      icon: null,
      options: [
        {
          value: "single",
          title: "Solo",
          display: "Solo",
          badge: "1 score",
          subtitle: "just one person, or to facilitate",
          description:
            "Only you, playing on your own — or for facilitating an exercise for a room (share your screen and drive an in-person tabletop). Good for solo practice, testing a scenario before you roll it out, or running it live for a group.",
        },
        {
          value: "group",
          title: "Independent Group",
          display: "Independent Group",
          badge: "1 score each",
          subtitle: "many people, each on their own",
          description:
            "Several people each run their OWN separate copy, on their own time — no live coordination. Each person is scored individually (compare them afterward on the group dashboard).",
        },
        {
          value: "multiplayer",
          title: "Collaborative",
          display: "Collaborative",
          badge: "1 shared team score",
          subtitle: "a team, together, live",
          description:
            "Your team plays ONE live incident together, at the same time. Each person takes a different role and works the same unfolding incident as a team — one shared score.",
        },
        {
          value: "facilitated",
          title: "Facilitated Roundtable",
          display: "Facilitated Roundtable",
          badge: "1 shared team score",
          subtitle: "one facilitator runs it for a room",
          description:
            "The facilitator logs in and runs the whole exercise for a room (e.g. an in-person tabletop), recording the team's decision for each role. The room's participants need no accounts or invites of their own — only the facilitator logs in. Best for discussion sessions.",
        },
        // Facilitated Roundtable is gated behind the FACILITATED_ROUNDTABLE org enablement
        // (default OFF) — hidden unless the org is granted it (user direction 2026-09-10).
      ].filter((option) => option.value !== "facilitated" || facilitatedEnabled),
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
      ],
      comments: "",
    })
    // Solo (single-role) can also be used to facilitate for a room — same toggle as the multi-role
    // Solo card; the facilitator fields render in the MAIN details form when it's on.
    if (singleRoleSetupValue === "single") {
      typeSelectionFields.push(buildSoloFacilitatorToggle({ currentValues, idSeed: "sr" }))
    }
    layoutGroups.push({
      view_order,
      id: "setup_type",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: typeSelectionFields.map(withCtfFormatWording),
    })
  }

  return {
    layout: "1-COLUMN",
    layout_data: [
      {
        id: "setup-type-layout-1",
        layout_order: 10,
        layout_width: 12,
        groups: layoutGroups,
      },
    ],
  }
}

// The CTF values the details step may hand on to the launch. Formik keeps the values of fields that are no
// longer in the layout, so a tick can outlive its field: the org picker moved to an org without CTF, or the
// user went back and picked a scenario that fails the question contract. Without the CTF settings in the
// layout the CTF keys are dropped (nothing sent — CTF stays as stored, like a never-shown group); on a
// failing scenario a tick that didn't come from this exercise's own stored config is cleared.
export const CTF_FORM_FIELD_NAMES = ["_ctfEnabled", "ctfMcqMode", "ctfTimeLimitSec", "ctfLeaderboard", "ctfSharedEvidence"]
export const ctfValuesForSubmit = ({ values, layoutHasCtf, contractFails, ctfAlreadyOnHere }) => {
  if (!values) return values
  if (!layoutHasCtf) {
    if (!CTF_FORM_FIELD_NAMES.some((k) => k in values)) return values
    const out = { ...values }
    for (const k of CTF_FORM_FIELD_NAMES) delete out[k]
    return out
  }
  if (contractFails && !ctfAlreadyOnHere && Array.isArray(values._ctfEnabled) && values._ctfEnabled.includes("ctf")) {
    return { ...values, _ctfEnabled: [] }
  }
  return values
}

// ---------------------------------------------------------------------------
// CTF Coordinator setup field group
// Pure helper — call inside gameSetupLayout and unit-test independently.
// Mirrors the shape of the inject-bot picker group (session_bots).
// ---------------------------------------------------------------------------
// `editingExisting` is true when the form saves back onto the SAME exercise (Edit, Restart): only then may
// a stored CTF-on value carry over onto a scenario that fails the question contract, so it can be seen
// and switched off. A new exercise or a Duplicate starts unticked on a failing scenario.
export const buildCtfCoordinatorFields = (session, scenario, { editingExisting = false } = {}) => {
  let ctf_field = 0
  const contractFails = scenario?.ctfContractOk === false
  const violations = scenario?.ctfContractViolations ?? []
  // A failing contract blocks only switching CTF ON. `uncheckOnly` disables just the unticked option
  // (input-checkbox), so a box that is already ticked — an existing CTF exercise whose scenario now fails,
  // or a failed contract check — can still be switched OFF. Disabling the whole field (`enabled: false`)
  // locked it on: Formik kept ["ctf"] and every save re-sent enabled:true.
  const storedOn = session?.ctfCoordinator?.enabled === true
  const startTicked = storedOn && (editingExisting || !contractFails)
  const enableComments = contractFails
    ? `CTF cannot be enabled: the following turns fail the question contract (each non-END turn must have exactly one correct action and at least two incorrect ones): ${violations.map((v) => v.turnId).join(", ")}.`
    : ""
  return [
    {
      id: "ctf_coordinator",
      card: "session",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "CTF Coordinator",
      fields: [
        {
          id: `ctf-field-${++ctf_field}`,
          width: 12,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "",
          name: "_ctfEnabled",
          placeholder: "",
          value: startTicked ? ["ctf"] : [],
          icon: null,
          options: [{ value: "ctf", display: "Enable CTF mode (timed, scored, leaderboard)" }],
          validations: [],
          uncheckOnly: contractFails ? true : undefined,
          comments: enableComments,
        },
        {
          id: `ctf-field-${++ctf_field}`,
          width: 12,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "Multiple choice questions",
          name: "ctfMcqMode",
          placeholder: "",
          // MULTI_OPTIONS carries an ARRAY of string option values — the renderer calls
          // .includes() on it and the schema is Yup.array().of(Yup.string()). A boolean here
          // crashes the tick handler and fails type validation even while hidden.
          value: session?.ctfCoordinator?.mcqMode ? ["mcq"] : [],
          icon: null,
          options: [{ value: "mcq", display: "Use multiple-choice question format" }],
          validations: [],
          comments: "",
          activeWhen: { field: "_ctfEnabled", is: "ctf" },
        },
        {
          id: `ctf-field-${++ctf_field}`,
          width: 12,
          type: FIELD_TYPES.DROPDOWN,
          label: "Time limit per turn",
          name: "ctfTimeLimitSec",
          placeholder: "",
          value: session?.ctfCoordinator?.timeLimitSec ?? 90,
          icon: null,
          options: [
            { value: 30, display: "30 seconds" },
            { value: 60, display: "60 seconds" },
            { value: 90, display: "90 seconds" },
            { value: 120, display: "120 seconds" },
            { value: 0, display: "Untimed" },
          ],
          validations: [],
          comments: "",
          activeWhen: { field: "_ctfEnabled", is: "ctf" },
        },
        {
          id: `ctf-field-${++ctf_field}`,
          width: 12,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "Leaderboard",
          name: "ctfLeaderboard",
          placeholder: "",
          // Array-of-string contract, as above. Defaults to on.
          value: (session?.ctfCoordinator?.leaderboard ?? true) ? ["leaderboard"] : [],
          icon: null,
          options: [{ value: "leaderboard", display: "Show leaderboard when the exercise ends" }],
          validations: [],
          comments: "",
          activeWhen: { field: "_ctfEnabled", is: "ctf" },
        },
        {
          id: `ctf-field-${++ctf_field}`,
          width: 12,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "Evidence sharing",
          name: "ctfSharedEvidence",
          placeholder: "",
          // Default ON: absent key on an existing session → shares evidence (safe default).
          // MULTI_OPTIONS array-of-strings — never a boolean; see leaderboard comment above.
          value: (session?.ctfCoordinator?.sharedEvidence ?? true) ? ["evidence"] : [],
          icon: null,
          options: [{ value: "evidence", display: "Share evidence across a phase (recommended)" }],
          validations: [],
          comments: "When on, later questions in a phase can still reference an earlier log. Resets between phases.",
          activeWhen: { field: "_ctfEnabled", is: "ctf" },
        },
      ],
    },
  ]
}

// Edit-load of an INDEPENDENT GROUP: GET /session on a group id carries no group signal or
// members, so merge GET /group's composed group (members, group name, the group's tags) into the
// loaded session. Returns `loadedSession` unchanged when `groupData` is not a group with members.
export const mergeGroupIntoLoadedSession = ({ loadedSession, groupData }) => {
  if (!groupData || !Array.isArray(groupData.sessions) || groupData.sessions.length === 0) return loadedSession
  const firstMember = groupData.sessions[0] ?? {}
  return {
    ...(loadedSession ?? {}),
    // scenario is the same across members; take it from a member when the single
    // session load didn't resolve (a group id is not a SESSION doc).
    test: loadedSession?.test ?? firstMember.test,
    name: groupData.name ?? loadedSession?.name,
    org: loadedSession?.org ?? groupData.org,
    groupName: groupData.name, // group signal for classifySetupTypeFromSession
    sessions: groupData.sessions, // members → the roster rebuild
    // KPI P1 (spec §11.3): the group's own tags (members inherit them) so the read-only
    // Edit field and a Duplicate show what the group was created with.
    groupTags: groupData.groupTags ?? firstMember.groupTags ?? loadedSession?.groupTags,
  }
}

export const gameSetupLayout = ({
  session,
  scenario,
  userAction,
  orgList,
  nlpModelList,
  setupType = "group",
  user = null,
  createGroupGame = null,
  botProfiles = null, // AI bot profile library ({key, name, trigger, enabled}) — managers only
  multiLanguageEnabled = false, // MULTI_LANGUAGE entitlement — hides ALL language UI when false
  ctfEnabled = false, // TARGET org's CTF entitlement. CTF is chosen when a scenario is built: a CTF scenario gets the details step's Challenge settings card (gated on this there); a tabletop scenario shows the CTF fields only on an Edit/Restart of an exercise that already has CTF on
  soloIsFacilitator = false, // Solo "facilitating for a room?" toggle is on → show facilitator fields
}) => {
  const scenarioRoles = rolesDefinedByScenario(scenario)
  // A CTF scenario (built on the builder's CTF path) gets the wizard's CTF setup instead of the tabletop
  // options: the Challenge settings card (rendered by the details step, not this layout) replaces the CTF
  // Coordinator fields, and the inject-bot picker and AI guidance mode — which change how a free-text
  // tabletop unfolds and is coached — are not offered.
  const isCtf = isCtfScenario(scenario)
  // Computed once for every role picker in this layout: the authoritative scoping list widened
  // with the scenario's DECLARED roles, which may name roles that no action carries.
  const roleSuggestions = roleSuggestionsForScenario(scenario)
  const scenarioOrgAccess = orgAccessForScenario(scenario)
  const isSingleRoleScenario = (scenarioRoles?.length ?? 0) === 1
  const layoutGroups = []
  let view_order = 10
  let game_detail = 0
  const isLayoutForDuplicate = TABLE_GAME_SCHEDULE_ACTION.DUPLICATE === userAction
  const isLayoutForRestart = TABLE_GAME_SCHEDULE_ACTION.RESTART === userAction
  const showOrgSelection = orgList && orgList.length
  const enableOrgSelection = !userAction // Only for new games
  const showDifficultySelection = !!isManager(user)
  const showNLPModelSelection = nlpModelList && nlpModelList.length
  // Player bots a manager can assign to a role (multi-role setup). Only player_action,
  // enabled bots.
  // NOTE: player-bot assignment moved OUT of the Players roster into the "AI Stakeholders"
  // section (AiSupportExtras region="support"), which works in ALL exercise types. The old
  // roster "Played by" switch/dropdown is therefore disabled here (allowRoleBotAssignment =
  // false); roleBots now come from AI Stakeholders via the ai_support_form slice. The
  // dropdown/switch field code below is left intact but gated off (single-flag removal).
  const assignableRoleBots =
    isManager(user) || isSponsor(user) || isTrainer(user)
      ? (botProfiles ?? []).filter((p) => p.enabled !== false && p.trigger === "player_action" && p.behavior === "autoplay")
      : []
  const allowRoleBotAssignment = false // was: assignableRoleBots.length > 0 — moved to AI Stakeholders

  // Only allow group game setup when creating or duplicating a game. Single-role scenarios use the
  // "group" setup type; a MULTI-role scenario uses "independentGroup" (each person independently
  // runs the whole scenario or a chosen role — the role picker lives in the format card).
  // EDIT round-trip: on EDIT/RESTART the format picker is hidden and the setupType is RESTORED from the
  // saved session (classifySetupTypeFromSession), so render the roster for that restored type too — not
  // only on create/duplicate. Without this, editing a Collaborative or Independent-Group exercise fell
  // into the solo/per-role branch and lost its roster.
  const isEditingSavedType = userAction === TABLE_GAME_SCHEDULE_ACTION.EDIT || userAction === TABLE_GAME_SCHEDULE_ACTION.RESTART

  const allowGroupGameSetup =
    (isSingleRoleScenario || setupType === "independentGroup") &&
    (TABLE_GAME_SCHEDULE_ACTION.DUPLICATE === userAction || !userAction || isEditingSavedType)

  // Has group been selected?
  // Note: createGroupGame is for backward compatibility if setupType not provided
  const groupGameSetup = allowGroupGameSetup && (setupType === "group" || setupType === "independentGroup" || createGroupGame === true)
  // Collaborative renders the SAME roster table (Import/Download CSV, add rows) for every
  // scenario. Single-role picker stores "multiplayer"; the multi-role picker used to store
  // "assigned" — treat that legacy value as collaborative too. (user direction 2026-09-08:
  // collaborative always shows the roster; multi-role seats are matched by the Role column.)
  const setupTypeSelectable = isLayoutForDuplicate || !userAction
  const isMultiplayer =
    // Restored Collaborative on EDIT/RESTART, OR create/duplicate selection (incl. legacy values).
    (setupType === "multiplayer" && (setupTypeSelectable || isEditingSavedType)) ||
    (setupTypeSelectable && !isSingleRoleScenario && (setupType === "assigned" || setupType === "group"))
  // Facilitated roundtable is available for ANY scenario (single- or multi-role): seats
  // are virtual, so no per-role people are collected — just the facilitator.
  const isFacilitatedSetup = setupType === "facilitated"
  // Track the Players table's view_order so the Observers table can be placed
  // directly beneath it (user directive) regardless of the other sections'
  // running view_order. Null for solo/single-role runs (no players table),
  // where Observers keeps its default trailing position.
  let playersViewOrder = null

  // Concrete exercise default language (English unless the session persisted another).
  // Every roster row's Language column SEEDS to this so the dropdown always shows a real
  // language — never a blank or an abstract "default" the user can't interpret. Rows the
  // user changes to a different language override it; a live effect re-seeds untouched rows
  // when the exercise-level Language changes (see new-game-form-details.jsx).
  const exerciseDefaultLang = normalizeLanguage(session?.defaultLanguage)

  // Fixed Game Setup
  // Organization Selection (Managers Only)
  if (showOrgSelection) {
    const orgSelectionFields = []
    orgSelectionFields.push({
      id: `game-detail-${++game_detail}`,
      width: 12,
      // Single-select dropdown (was radio-button OPTIONS). Consistent for all roles.
      type: FIELD_TYPES.DROPDOWN,
      label: `Choose the organization`,
      name: "_orgId",
      placeholder: "",
      value: session?.org ?? "",
      enabled: enableOrgSelection,
      icon: null,
      options: [
        // Leading placeholder so an unselected dropdown (value "") shows a prompt rather
        // than silently defaulting to the first org. Only for new exercises where no org
        // is preset; REQUIRED validation forces a real choice.
        ...(enableOrgSelection && !session?.org ? [{ value: "", display: "Select an organization" }] : []),
        ...orgList
          .filter((orgObj) => {
            // Restrict to orgs with access to the scenario. An empty (or null) orgAccess
            // means GLOBAL (all orgs) — matching ScenarioCard/ConfigureScenarioModal — so
            // only filter when there is a non-empty allow-list; otherwise every owned org
            // is eligible. (Without the length check, a global scenario whose orgAccess is
            // [] — e.g. a partner's null→[] redaction — would show an empty picker.)
            if (scenarioOrgAccess && scenarioOrgAccess.length > 0) {
              return scenarioOrgAccess.includes(orgObj.id)
            }
            return true
          })
          .map((orgObj) => ({
            value: orgObj.id,
            display: orgObj.displayName || orgObj.id,
          })),
      ],
      validations: enableOrgSelection
        ? [
            {
              type: VALIDATION_TYPES.REQUIRED,
              error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
            },
          ]
        : [],
      comments: "",
    })
    layoutGroups.push({
      view_order,
      id: "game_organization",
      card: "basics",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: orgSelectionFields,
    })
  }
  // Exercise/Group Name (Only for new or duplicate exercises)
  if (!session || isLayoutForDuplicate) {
    const gameOrGroupLabel = groupGameSetup ? "Group of Exercises" : "Exercise"
    const gameNameFields = []
    gameNameFields.push({
      id: `game-detail-${++game_detail}`,
      width: 6,
      type: FIELD_TYPES.STRING,
      label: isLayoutForDuplicate ? `${gameOrGroupLabel} name (duplicate)` : `${gameOrGroupLabel} name`,
      name: "_name",
      placeholder: `A Unique Name For This ${gameOrGroupLabel}`,
      value: groupGameSetup
        ? defaultGroupGameName({ scenario: scenario })
        : defaultGameName({ duplicatingSessionId: isLayoutForDuplicate ? (session?.name ?? "existing") : null, scenario: scenario }),
      icon: null,
      options: [],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
        {
          type: VALIDATION_TYPES.SAFE_API_NODE_NAME,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFE_API_NODE_NAME,
        },
        {
          type: VALIDATION_TYPES.MAX_LENGTH,
          value: validAPINodeFieldMaxLength,
          error_message: errorMessageReplacer(
            VALIDATION_TYPES.MAX_LENGTH,
            LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH,
            validAPINodeFieldMaxLength
          ),
        },
      ],
      comments: "",
    })
    layoutGroups.push({
      view_order,
      id: "game_name",
      card: "basics",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: gameNameFields,
    })
  }
  // Scheduled time
  view_order += 10
  let existingValue
  if (isLayoutForDuplicate || isLayoutForRestart) {
    existingValue = "" // Don't include scheduled date from original game session
  } else {
    existingValue = session?.scheduledTime ? moment(new Date(session.scheduledTime)).format(`${DATE_FORMAT} ${TIME_FORMAT}`) : ""
  }
  const startTimeFields = []
  startTimeFields.push({
    id: `game-detail-${++game_detail}`,
    width: 6,
    type: FIELD_TYPES.DATE,
    native: true, // Render as native <input type="datetime-local"> (flattened mock look)
    label: `Start date & time (${moment(new Date()).tz(moment.tz.guess(true)).format("z")})`,
    helper: "Leave empty to start immediately",
    name: "start_date",
    placeholder: "Select Date",
    value: existingValue,
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.FUTURE_DATETIME,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.FUTURE_DATETIME,
      },
    ],
    comments: "",
  })
  // startTimeFields.push({
  //   id: `game-detail-${++game_detail}`,
  //   width: 2,
  //   type: FIELD_TYPES.TIME,
  //   label: 'Time',
  //   name: 'start_time',
  //   placeholder: 'Select Time',
  //   value: (session?.scheduledTime ? moment(new Date(session.scheduledTime)).format(TIME_FORMAT) : ''),
  //   icon: null,
  //   options: [],
  //   validations: [
  //     // {
  //     //   type: VALIDATION_TYPES.REQUIRED,
  //     //   error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED
  //     // }
  //   ],
  //   comments: ''
  // })
  // startTimeFields.push({
  //   id: `game-detail-${++game_detail}`,
  //   width: 2,
  //   type: FIELD_TYPES.DROPDOWN,
  //   label: 'Time Zone',
  //   name: 'time_zone',
  //   placeholder: '',
  //   value: 'EST',
  //   icon: null,
  //   options: [],
  //   validations: [
  //     // {
  //     //   type: VALIDATION_TYPES.REQUIRED,
  //     //   error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED
  //     // }
  //   ],
  //   comments: ''
  // })
  layoutGroups.push({
    view_order,
    id: "game_start_schedule",
    card: "basics",
    type: FIELD_GROUP_TYPES.FIELDS,
    label: "",
    fields: startTimeFields,
  })

  if (showDifficultySelection) {
    // Difficulty Settings
    view_order += 10
    let existingNLPAutoDisableThreshold = session?.nlpAutoDisableThreshold ?? 0 // Default to 'Never' (0)
    let existingNLPEnabled = session?.nlpEnabled ?? "true" // Default to 'Normal' (true)
    const difficultyFields = []
    difficultyFields.push({
      id: `game-detail-${++game_detail}`,
      width: 12,
      type: FIELD_TYPES.OPTIONS,
      label: `Exercise Difficulty`,
      name: GAME_SETUP_TYPE_FORM_FIELDS.NLP_MODE_BOOLEAN,
      placeholder: "",
      value: `${existingNLPEnabled}`,
      icon: null,
      options: [
        { value: "true", display: "Normal" },
        { value: "false", display: "Easy (Show Options as Multiple Choice)" },
      ],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
      ],
      comments: "",
    })

    difficultyFields.push({
      id: `game-detail-${++game_detail}`,
      width: 12,
      type: FIELD_TYPES.OPTIONS,
      label: `Assist players by switching turn to multiple choice after consecutive empty action search results? (Easy Mode)`,
      name: GAME_SETUP_TYPE_FORM_FIELDS.NLP_AUTO_DISABLE_THRESHOLD,
      placeholder: "",
      value: `${existingNLPAutoDisableThreshold}`,
      icon: null,
      options: [
        { value: "0", display: "Never" },
        { value: "1", display: "1 search" },
        { value: "2", display: "2 searches" },
        { value: "3", display: "3 searches" },
        { value: "4", display: "4 searches" },
        { value: "5", display: "5 searches" },
        { value: "6", display: "6 searches" },
        { value: "7", display: "7 searches" },
        { value: "8", display: "8 searches" },
        { value: "9", display: "9 searches" },
        { value: "10", display: "10 searches" },
      ],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
      ],
      comments: "",
    })
    layoutGroups.push({
      view_order,
      id: "difficulty_settings",
      card: "basics",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: difficultyFields,
    })
  }

  // NLP Model Settings
  if (showNLPModelSelection) {
    view_order += 10
    const nlpModelFields = []
    nlpModelFields.push({
      id: `game-detail-${++game_detail}`,
      width: 12,
      // DROPDOWN (native select, type-to-jump), not OPTIONS — the merged Bedrock catalog
      // makes a radio grid fill the screen
      type: FIELD_TYPES.DROPDOWN,
      label: `Choose an AI Model`,
      name: "_nlpModel",
      placeholder: "",
      value: session?.nlpModel ?? getReplaneConfig("frontend", "ai.nlp_model", defaultSessionNLPModel),
      icon: null,
      options: nlpModelList
        .filter((nlpModelObj) => nlpModelObj.deprecated !== true)
        .map((nlpModelObj) => ({
          value: nlpModelObj.name,
          display: `${nlpModelObj.name} (${nlpModelObj.type})`,
        })),
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
      ],
      comments: "",
    })
    layoutGroups.push({
      view_order,
      id: "game_nlp_model",
      card: "basics",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "",
      fields: nlpModelFields,
    })
    view_order += 10
    const nlpContextFields = []
    nlpContextFields.push({
      id: `game-detail-${++game_detail}`,
      width: 12,
      // DROPDOWN, not OPTIONS — same reason as the AI model picker above
      type: FIELD_TYPES.DROPDOWN,
      label: `AI Model For Matched Actions Prescreen`,
      name: GAME_SETUP_TYPE_FORM_FIELDS.NLP_MODEL_PROMPT_MATCHED_ACTIONS_PRESCREEN,
      placeholder: "",
      value: session?.nlpContext?.nlpModelPromptMatchedActionsPrescreen ?? defaultSessionNLPModelPromptMatchedActionsPrescreen,
      icon: null,
      options: [
        {
          value: defaultSessionNLPModelPromptMatchedActionsPrescreen,
          display: `Use Default (${defaultSessionNLPModelPromptMatchedActionsPrescreenDisplayName})`,
        },
      ].concat(
        nlpModelList
          .filter((nlpModelObj) => nlpModelObj.deprecated !== true && nlpModelObj.type !== nlpModelTypeLocal)
          .map((nlpModelObj) => ({
            value: nlpModelObj.name,
            display: `${nlpModelObj.name} (${nlpModelObj.type})`,
          }))
      ),
      validations: [
        // {
        //   type: VALIDATION_TYPES.REQUIRED,
        //   error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        // },
      ],
      comments: "",
    })
    layoutGroups.push({
      view_order,
      id: "game_nlp_context",
      card: "basics",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "Advanced AI Model Settings",
      fields: nlpContextFields,
    })
  }

  // Single Role Scenario
  if (groupGameSetup) {
    // Add a CSV file field for creating a group of games
    view_order += 10
    const csvFileFields = []

    csvFileFields.push({
      id: `game-detail-${++game_detail}`,
      width: 4,
      type: FIELD_TYPES.STRING,
      label: "Name",
      name: "name",
      placeholder: "",
      value: "",
      icon: null,
      options: [],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
        {
          type: VALIDATION_TYPES.SAFENAME,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
        },
        {
          type: VALIDATION_TYPES.MAX_LENGTH,
          value: validNameFieldMaxLength,
          error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
        },
      ],
      comments: "",
    })

    csvFileFields.push({
      id: `game-detail-${++game_detail}`,
      width: 4,
      type: FIELD_TYPES.EMAIL,
      label: "Email",
      name: "email",
      placeholder: "",
      value: "",
      icon: null,
      options: [],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
        {
          type: VALIDATION_TYPES.EMAIL,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
        },
        {
          type: VALIDATION_TYPES.UNIQUE,
          error_message: "Duplicate email addresses are not allowed",
        },
      ],
      comments: "",
    })

    // Independent Group: EVERY scenario gets the Role field (user direction 2026-09-16 — the control
    // must never be invisibly "missing"). Each person runs their OWN separate copy and types the role
    // they play — a FREE-TEXT field with the scenario roles + "All roles" as type-ahead suggestions
    // (user direction 2026-09-18). The typed role is resolved server-side to a scenario role
    // (title/abbreviation tolerant); blank or "All roles" == whole incident for that member. Drives the
    // per-member soloRoles the server fan-out stamps. No Facilitator option. Resolution is shown in the
    // bottom "Scored on" summary, not inline.
    // CTF scenarios have no roles to play: every player answers every question, so the roster is
    // Name + Email only (blank role == whole incident server-side). Tabletop keeps the Role column.
    if (!isCtf)
      csvFileFields.push({
        id: `game-detail-${++game_detail}`,
        width: 3,
        type: FIELD_TYPES.ROLE_INPUT,
        label: "Role",
        name: "role",
        placeholder: `Role or title — or "${ALL_ROLES_LABEL}"`,
        value: "",
        icon: null,
        suggestions: [ALL_ROLES_LABEL, ...roleSuggestions],
        validations: [],
        comments: "",
      })

    // Language — per-player display language. Seeded to the concrete exercise default
    // (English by default) so the dropdown always shows a real language; a row set to a
    // different code overrides. The group roster rows flow straight to players[] (server
    // spreads the row), so `language` rides along without a separate mapping. Only rendered
    // for MULTI_LANGUAGE-entitled orgs — otherwise the column is absent and every row
    // resolves to English at submit.
    if (multiLanguageEnabled) {
      csvFileFields.push({
        id: `game-detail-${++game_detail}`,
        width: 2,
        type: FIELD_TYPES.DROPDOWN,
        label: "Language",
        name: "language",
        placeholder: "",
        value: exerciseDefaultLang,
        icon: null,
        options: ROW_LANGUAGE_OPTIONS.map((o) => ({ ...o })),
        validations: [],
        comments: "",
      })
    }

    playersViewOrder = view_order
    layoutGroups.push({
      view_order,
      id: "group_player_list",
      card: "players",
      type: FIELD_GROUP_TYPES.ROSTER,
      export_enabled: true,
      import_enabled: true,
      label: `Players`,
      hint: isCtf
        ? "Each player answers every question on their own screen and is scored separately — no roles needed. In a live event everyone gets each question at the same moment; self-paced players go at their own pace."
        : "Each player runs the scenario on their own time and is scored separately.",
      name: GROUP_GAME_FORM_FIELDS.PLAYER_LIST,
      // Edit/Duplicate: `_groupPlayers` is never persisted on the group, so fall back to
      // rebuilding the roster from the group's member sessions (each carries its own soloRole +
      // name/email/title/language). CREATE has no member sessions → null → a single blank seed row.
      value: rosterValueWithSeed(
        session?.[GROUP_GAME_FORM_FIELDS.PLAYER_LIST] ?? (isEditingSavedType ? groupRosterFromSession({ session }) : null),
        csvFileFields
      ),
      fields: csvFileFields,
      validations: [
        {
          type: VALIDATION_TYPES.MIN_LENGTH,
          value: 1,
          error_message: "At least one player must be added",
        },
      ],
    })

    // // Batch size for game group dispatch
    // view_order += 10
    // const batchFields = []
    // batchFields.push({
    //   id: `game-detail-${++game_detail}`,
    //   width: 12,
    //   type: FIELD_TYPES.OPTIONS,
    //   label: '',
    //   name: GROUP_GAME_FORM_FIELDS.BATCH_SIZE,
    //   placeholder: "",
    //   value: "10",
    //   icon: null,
    //   options: [
    //     {
    //       value: "1",
    //       display: "1 Game"
    //     },
    //     {
    //       value: "5",
    //       display: "5 Games"
    //     },
    //     {
    //       value: "10",
    //       display: "10 Games"
    //     },
    //     {
    //       value: "50",
    //       display: "50 Games"
    //     }
    //   ],
    //   validations: [
    //     {
    //       type: VALIDATION_TYPES.REQUIRED,
    //       error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED
    //     }
    //   ],
    //   comments: ""
    // })

    // layoutGroups.push({
    //   view_order,
    //   id: 'group_batch_config',
    //   type: FIELD_GROUP_TYPES.FIELDS,
    //   label: "Choose a game group batch size for staggering start times",
    //   fields: batchFields
    // })

    // // Batch staggered start delay
    // view_order += 10
    // const batchIntervalFields = []
    // batchIntervalFields.push({
    //   id: `game-detail-${++game_detail}`,
    //   width: 12,
    //   type: FIELD_TYPES.OPTIONS,
    //   label: '',
    //   name: GROUP_GAME_FORM_FIELDS.BATCH_START_INTERVAL_MINUTES,
    //   placeholder: "",
    //   value: "30", // minutes
    //   icon: null,
    //   options: [
    //     {
    //       value: "0",
    //       display: "None"
    //     },
    //     {
    //       value: "30",
    //       display: "30 Minutes"
    //     },
    //     {
    //       value: "60",
    //       display: "1 Hour"
    //     },
    //     {
    //       value: "1440",
    //       display: "24 Hours"
    //     }
    //   ],
    //   validations: [
    //     {
    //       type: VALIDATION_TYPES.REQUIRED,
    //       error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED
    //     }
    //   ],
    //   comments: ""
    // })

    // layoutGroups.push({
    //   view_order,
    //   id: 'group_batch_interval',
    //   type: FIELD_GROUP_TYPES.FIELDS,
    //   label: "Choose a time interval for staggering batch start times",
    //   fields: batchIntervalFields
    // })
  } else if (isFacilitatedSetup) {
    // Facilitated roundtable: per-role people rows are intentionally SKIPPED (the
    // roster is virtual seats). The facilitator card itself is pushed LATER — in the
    // same slot the Players table occupies in group/collaborative modes: below the
    // AI sections, directly above Observers (user directive 2026-09-07).
  } else if (!isMultiplayer) {
    // Role Assignment Groups / Fields (Skip for multiplayer games).
    // Visually rendered as the mock's bottom "Players" table: NAME / EMAIL / ROLE / FACILITATOR.
    // This is a presentation-only restructure — the field NAMES are unchanged
    // (`{role}_name`/`{role}_email`/`{role}_title`/`{role}_isFacilitator` via paramNameForRole)
    // so setupParametersForScenarioFromGameDetailsValues still builds roleAssignments/
    // roleAssigneeNames/roleAssigneeTitles exactly as today.
    //
    // SOLO IS ONE PLAYER (user direction 2026-09-14): a Solo run is a single person — who can be
    // anybody, not the creator — so it renders exactly ONE editable Name/Email row, never one row
    // per scenario role. The row binds to an "anchor" scenario role so the existing per-role submit
    // path is unchanged: for one-role scope the anchor IS the chosen role (that role is what's
    // played/scored server-side via soloRoles); for whole-scenario scope the anchor is the first
    // role, which is enough to carry the player's email into roleAssignments so the participant
    // access gate passes (the single player then plays every role's turns — whole-incident score).
    // Every other non-multiplayer flow (single-role assigned/group) keeps one-row-per-role.
    // Both one-person modes render as a single scored player row: multi-role scenarios use "solo",
    // single-role scenarios use "single". They must be treated identically here — otherwise the
    // single-role Solo, which now also has the dedicated facilitator toggle, would keep the legacy
    // per-row Facilitator checkbox AND the top-level Language group (double controls) on top of it.
    const isSoloOnePlayer = setupType === "solo" || setupType === "single"
    // Solo is ONE player: render exactly one row bound to a fixed anchor role (the first scenario
    // role) purely to carry the player's Name/Email into roleAssignments so the participant access
    // gate passes. The role the player actually plays/is scored on is chosen in the row's Role
    // selector (All roles / a specific role / Facilitator) — NOT by which anchor we bind to — so the
    // anchor is a stable implementation detail, never the stale top-level _soloRole.
    const soloAnchorRole = scenarioRoles?.[0]
    // Solo is ALWAYS one scored player row (Name/Email/Role/Title/Language), whether or not a
    // facilitator runs it (user direction 2026-09-14). "Facilitating for a room? = Yes" ADDS a
    // facilitator block below (facilitator watches the live board + pushes injects) but does NOT
    // change the player — the single player still plays and is scored exactly like plain Solo.
    const rosterRoles = isSoloOnePlayer ? [soloAnchorRole].filter(Boolean) : scenarioRoles
    // EDIT seed for the sole Solo player. A ROLE-SCOPED Solo stores roleAssignments/names keyed under
    // the PLAYED role (e.g. "IT Lead"), not the anchor (scenarioRoles[0] = "Legal"), because the
    // submit path re-keys the map. Seeding the row's Name/Email from roleAssignments[anchor] would
    // therefore blank them and lose the participant on the next save (which now REQUIREs them).
    // Resolve the one player from whatever key holds them (they're all the same person), falling back
    // to players[0]. Used only for the Solo one-player row; multi-seat rows keep their per-role seed.
    const soloEditPlayers = Array.isArray(session?.players) ? session.players : []
    const soloEditRoleKey = Object.keys(session?.roleAssignments ?? {}).find((r) => session.roleAssignments[r]) ?? null
    const soloEditEmail = (soloEditRoleKey ? session?.roleAssignments?.[soloEditRoleKey] : null) ?? soloEditPlayers[0]?.email ?? ""
    const soloEditName = (soloEditRoleKey ? session?.roleAssigneeNames?.[soloEditRoleKey] : null) ?? soloEditPlayers[0]?.name ?? ""
    // The Solo row's Role selector is a MULTI-select (checkboxes) built inline below: each scenario
    // role is an option; none checked == whole incident. Facilitator is NOT a role option — it stays
    // the separate "facilitating for a room?" flow (user direction 2026-09-14: don't mix "which role
    // you play" with "you run it instead of playing").
    rosterRoles.forEach((roleName, roleIdx) => {
      view_order += 10
      const roleFields = []
      // Multi-role only: let a manager mark this role as played by an AUTOPLAY bot instead
      // of a human. When a bot is chosen, the row's Name/Email are hidden and not required
      // (the bot has no human identity) via `activeWhen`; the choice maps to roleBots on submit.
      const roleBotSelectable = allowRoleBotAssignment && rosterRoles.length > 1
      const playAsName = paramNameForRole({ role: roleName, param: "playAs" })
      if (roleBotSelectable) {
        roleFields.push({
          id: `game-detail-${++game_detail}`,
          width: 3,
          type: FIELD_TYPES.DROPDOWN,
          label: "Played by",
          name: playAsName,
          placeholder: "",
          value: session?.roleBots?.[roleName] ?? "human",
          icon: null,
          options: [
            { value: "human", display: "Live responder" },
            ...assignableRoleBots.map((p) => ({ value: p.key, display: `${(p.name || p.key).replace(/\s*\(AI\)\s*$/i, "").trim()} (AI)` })),
          ],
          validations: [{ type: VALIDATION_TYPES.REQUIRED, error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED }],
          comments: "",
        })
      }
      roleFields.push({
        id: `game-detail-${++game_detail}`,
        width: roleBotSelectable ? 3 : 3,
        type: FIELD_TYPES.STRING,
        label: "Name",
        name: paramNameForRole({ role: roleName, param: "name" }),
        placeholder: `Name`,
        value: isSoloOnePlayer ? soloEditName : (session?.roleAssigneeNames?.[roleName] ?? ""),
        icon: null,
        options: [],
        ...(roleBotSelectable ? { activeWhen: { field: playAsName, is: "human" } } : {}),
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
          },
          {
            type: VALIDATION_TYPES.SAFENAME,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
          },
          {
            type: VALIDATION_TYPES.MAX_LENGTH,
            value: validNameFieldMaxLength,
            error_message: errorMessageReplacer(
              VALIDATION_TYPES.MAX_LENGTH,
              LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH,
              validNameFieldMaxLength
            ),
          },
        ],
        comments: "",
      })
      roleFields.push({
        id: `game-detail-${++game_detail}`,
        width: roleBotSelectable ? 3 : 3,
        type: FIELD_TYPES.EMAIL,
        label: "Email",
        name: paramNameForRole({ role: roleName, param: "email" }),
        placeholder: `email@company.com`,
        value: isSoloOnePlayer ? soloEditEmail : (session?.roleAssignments?.[roleName] ?? ""),
        icon: null,
        options: [],
        ...(roleBotSelectable ? { activeWhen: { field: playAsName, is: "human" } } : {}),
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
          },
          {
            type: VALIDATION_TYPES.EMAIL,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
          },
        ],
        comments: "",
      })
      if (isSoloOnePlayer) {
        // CTF: no Role selector — the one player answers every question (blank == whole incident).
        if (!isCtf) {
          // ROLE selector — what this player actually plays / is scored on. Replaces the removed
          // top-level "What do you play?" choice. All roles (whole incident) · a specific scenario
          // role (scored on it). The picker renders for EVERY scenario (user direction 2026-09-16): a
          // single-role scenario simply offers its one role as the option (picking it == All roles ==
          // the whole incident), so the control is never invisibly missing (see the block below).
          // Seed from the saved session on edit: a pinned soloRole → that role; otherwise All roles.
          {
            // The Role dropdown renders for EVERY scenario (user direction 2026-09-16) — a single-role
            // scenario shows its one role as the option (picking it == All roles == the whole incident),
            // so the control is never invisibly "missing". MULTI-select: check one or more scenario
            // roles to be scored ONLY on those; All roles == the whole incident. soloRoles is an array
            // end-to-end and scoring filters by soloRoles.includes(reportItem.role).
            // Defensive: coerce a scalar-shaped stored value to a one-item array before filtering, so a
            // hand-written/legacy `soloRoles: "IT Lead"` doesn't open as All-roles and silently widen
            // the scope on the next save (the submit mapper accepts the scalar shape the same way).
            // Seed the free-text field from the saved scope: the sole stored soloRole (the TYPED value
            // that was saved — the server persists what was sent), or "" for whole incident. Tolerate a
            // legacy scalar or array shape.
            const storedSoloRoles = Array.isArray(session?.soloRoles) ? session.soloRoles : session?.soloRoles ? [session.soloRoles] : []
            const soloRoleSeed = roleFieldValueFromSoloRoles(storedSoloRoles) // "" = All roles; several (legacy) → "A, B" (lossless)
            roleFields.push({
              id: `game-detail-${++game_detail}`,
              // Title removed 2026-09-18 → Role widens to fill the freed columns: with multiLanguage the
              // row is Name(3)+Email(3)+Role(4)+Language(2)=12; without it Name+Email+Role fill the row.
              width: 4,
              // FREE-TEXT role (user direction 2026-09-18): type a role or title; the scenario roles +
              // "All roles" are type-ahead suggestions. The server resolves the typed role to a scenario
              // role (exact → normalized → synonym → semantic); blank / "All roles" == whole incident.
              // Resolution is shown in the bottom "Scored on" summary, not inline.
              type: FIELD_TYPES.ROLE_INPUT,
              label: "Role",
              name: paramNameForRole({ role: roleName, param: "playerRole" }),
              placeholder: `Role or title — or "${ALL_ROLES_LABEL}"`,
              value: soloRoleSeed,
              icon: null,
              suggestions: [ALL_ROLES_LABEL, ...roleSuggestions],
              validations: [],
              comments: "",
            })
          }
        }
        // (Title column removed 2026-09-18 — redundant now that Role is free-text. The person's
        // display label falls back to the resolved role name; existing stored titles are untouched.)
      } else {
        // Single-role (non-solo): free-form label captured as the job title on submit.
        roleFields.push({
          id: `game-detail-${++game_detail}`,
          width: roleBotSelectable ? 2 : 2,
          type: FIELD_TYPES.STRING,
          label: "Role",
          name: paramNameForRole({ role: roleName, param: "title" }),
          placeholder: "Role",
          value: session?.roleAssigneeNamesTitles?.[roleName] ?? "",
          icon: null,
          options: [],
          validations: [],
          comments: "",
        })
      }
      // Language — per-seat display language. For solo/single-role this IS the player's
      // language. Seeded to a concrete language (the saved per-role language on
      // edit/round-trip, else the exercise default — English by default) so the dropdown
      // always shows a real language. Canonical gameplay content stays English. Only
      // rendered for MULTI_LANGUAGE-entitled orgs.
      if (multiLanguageEnabled) {
        roleFields.push({
          id: `game-detail-${++game_detail}`,
          width: 2,
          type: FIELD_TYPES.DROPDOWN,
          label: "Language",
          name: paramNameForRole({ role: roleName, param: "language" }),
          placeholder: "",
          value: seededRoleLanguage({ session, roleName }),
          icon: null,
          options: ROW_LANGUAGE_OPTIONS.map((o) => ({ ...o })),
          validations: [],
          comments: "",
        })
      }
      // Facilitator — per-row toggle for SINGLE-ROLE (non-solo) rosters only. SOLO no longer has a
      // per-row Facilitator checkbox (user direction 2026-09-14): facilitation is the single top
      // "Are you facilitating this for a room?" control, so the Solo row is just Name/Email/Role/
      // Title/Language (no wrap, no dual facilitator mechanism).
      if (!isSoloOnePlayer) {
        roleFields.push({
          id: `game-detail-${++game_detail}`,
          width: 1,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "Facilitator",
          name: paramNameForRole({ role: roleName, param: "isFacilitator" }),
          placeholder: "",
          // Multi-facilitator: pre-check this role's Facilitator box if the role's assignee email
          // is ANY of the session's facilitators (co-equal array + legacy single binding).
          value:
            session?.roleAssignments?.[roleName] && sessionFacilitatorEmailSet(session).has(session.roleAssignments[roleName].toLowerCase())
              ? ["facilitator"]
              : [],
          icon: null,
          options: [{ value: "facilitator", display: "" }],
          validations: [],
          comments: "",
        })
      }
      layoutGroups.push({
        view_order,
        id: paramNameForRole({ role: roleName, param: "detail" }),
        // Solo/single-role player rows render in the "Players" table region, ABOVE
        // the Observers table — see new-game-form-details.jsx. Styled to read like
        // the mock's Players row (NAME/EMAIL/ROLE/FACILITATOR).
        card: "soloplayer",
        type: FIELD_GROUP_TYPES.FIELDS,
        // Wrap the field columns in a bordered container so the row reads as the
        // mock's Players table (header labels + inputs inside one bordered box).
        fieldsWrapperClass: "ctd-soloplayer-tablebody",
        // Heading + hint only on the first role row so the table has one header. When Solo is
        // "facilitating for a room", this single row IS the facilitator — that person runs it live
        // (Facilitator View + injects) AND is the scored participant — so the section reads
        // "Facilitator", not "Players" (no separate player seat; user direction 2026-09-14).
        label: roleIdx === 0 ? (isSoloOnePlayer && soloIsFacilitator ? "Facilitator" : "Players") : "",
        hint:
          roleIdx === rosterRoles.length - 1
            ? isSoloOnePlayer
              ? soloIsFacilitator
                ? "This person runs the exercise live for the room — they get the Facilitator View and can push injects — and their play is the single scored session. On a multi-role scenario, type the Role they run (All roles or a specific one). Add co-facilitators below."
                : "One player — enter who is playing (can be anyone). On a multi-role scenario, type the Role they play (All roles or a specific one). To have someone run it live for a room, use the facilitator toggle above — the player is still scored."
              : "Each player gets a different role from the scenario — the invite explains theirs. Optionally mark one or more players as facilitators — they run the session and aren't scored."
            : undefined,
        fields: roleFields,
      })
    })
  }

  // The new-vs-classic gameplay UI is no longer chosen here. It moved to a
  // per-user preference (privileged roles) with a Replane-driven default; see
  // Container.jsx's redirect effect and Settings. The session field
  // `useNewGameplayExperience` is kept for back-compat but is no longer set here.
  // "Show live timeline and facts to all players" (executiveDetailsEnabled) is now ALWAYS ON —
  // the toggle was removed (user direction 2026-09-10). Every exercise reveals the unfolding
  // incident to all participants; the value is forced true at submit (see executiveDetailsEnabled
  // in setupParametersForScenarioFromGameDetailsValues). No "Session options" group renders here.

  // Facilitator inject bot (Managers only) — a bot on the facilitator_inject trigger that
  // narrates pushed injects through its own prompt. React bots ("AI stakeholders") and
  // autoplay bots ("AI Teammates") have their own controls, so this picker is limited to
  // inject-type bots only.
  const selectableBots =
    isManager(user) || isSponsor(user) || isTrainer(user)
      ? (botProfiles ?? []).filter((p) => p.enabled !== false && p.trigger === "facilitator_inject")
      : []
  if (selectableBots.length && !isCtf) {
    view_order += 10
    layoutGroups.push({
      view_order,
      id: "session_bots",
      card: "session", // render in Card 3 · Session & AI support (flattened wizard)
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "Facilitator inject bot",
      fields: [
        {
          id: `game-detail-${++game_detail}`,
          width: 12,
          type: FIELD_TYPES.MULTI_OPTIONS,
          label: "",
          name: "_sessionBots",
          placeholder: "",
          value: (session?.bots ?? []).filter((key) => selectableBots.some((p) => p.key === key)),
          icon: null,
          options: selectableBots.map((p) => ({ value: p.key, display: p.name || p.key })),
          validations: [],
          comments: "",
        },
      ],
    })
  }

  // CTF Coordinator — optional; visible to managers, sponsors, and trainers (same guard as
  // selectableBots). When enabled, adds timed/scored/leaderboard CTF settings to the exercise.
  // Gated on the TARGET org's CTF entitlement. Only an Edit or Restart (which save back onto the same
  // exercise, so the server compares against its stored config) still shows the settings for an exercise
  // that already has CTF on, so it can be switched off after the entitlement is removed — the server never
  // refuses switching off or re-saving. A Duplicate creates a NEW exercise, which the server gates like any
  // create: without the entitlement the settings stay hidden and the copy is made with CTF off.
  const savesOntoSameExercise = userAction === TABLE_GAME_SCHEDULE_ACTION.EDIT || userAction === TABLE_GAME_SCHEDULE_ACTION.RESTART
  const ctfAlreadyOnHere = savesOntoSameExercise && session?.ctfCoordinator?.enabled === true
  // CTF is chosen when a scenario is BUILT (the builder's CTF path), not per exercise: a tabletop scenario
  // no longer offers these fields, except on an Edit/Restart of an exercise that already has CTF on, so it
  // can still be seen and switched off. A CTF scenario uses the Challenge settings card instead. The CTF
  // entitlement (ctfEnabled) gates that card in the details step.
  if (!isCtf && ctfAlreadyOnHere && (isManager(user) || isSponsor(user) || isTrainer(user))) {
    // Pass the selected scenario so the toggle can't be switched ON when it fails the CTF question
    // contract. The scenario carries ctfContractOk/ctfContractViolations only when the setup
    // fetch requested includeCtfContract; absent info leaves the toggle enabled (back-compat).
    const ctfGroups = buildCtfCoordinatorFields(session, scenario, { editingExisting: savesOntoSameExercise })
    for (const g of ctfGroups) {
      view_order += 10
      layoutGroups.push({ ...g, view_order })
    }
  }

  // Guidance Mode — applies to all exercise types (solo, group, multiplayer). Not for a CTF scenario: it
  // changes how the AI coaches free-text answers, and a CTF is answered from multiple-choice tiles.
  if (!isCtf) {
    view_order += 10
    layoutGroups.push({
      view_order,
      id: "guidance_mode",
      card: "session",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "AI guidance mode",
      fields: [
        {
          id: `game-detail-${++game_detail}`,
          width: 12,
          type: FIELD_TYPES.DROPDOWN,
          label: "AI guidance mode",
          name: MULTIPLAYER_FORM_FIELDS.GUIDANCE_MODE,
          placeholder: "",
          value: session?.guidanceMode ?? "assessment",
          icon: null,
          options: [
            { value: "assessment", display: "Assessment (Audit)" },
            { value: "guided", display: "Guided (Training)" },
          ],
          helper: "Assessment tests what the team knows (the AI asks them to be specific); Guided has the AI coach with answers.",
          validations: [],
          comments: "",
        },
      ],
    })
  }

  // Exercise Language — applies to all exercise types. Defaults visibly to English and
  // SEEDS each roster row's per-player Language column; rows stay individually overridable.
  // For solo it is the player's language. Canonical gameplay content stays English;
  // this only drives translated display via the server translation layer. Only rendered
  // for MULTI_LANGUAGE-entitled orgs — otherwise the whole group is absent and everyone
  // resolves to English at submit.
  if (multiLanguageEnabled && setupType !== "solo" && setupType !== "single") {
    // Not shown for SOLO (user direction 2026-09-14): a Solo exercise has ONE language, set on the
    // player row (No) or the facilitator block (facilitating a room = Yes) — no separate top-level
    // exercise Language. Collaborative / Independent Group keep it as a default across many rows.
    view_order += 10
    layoutGroups.push({
      view_order,
      id: "exercise_language",
      card: "session",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "Language",
      fields: [
        {
          id: `game-detail-${++game_detail}`,
          width: 12,
          type: FIELD_TYPES.DROPDOWN,
          label: "Language",
          name: MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE,
          placeholder: "",
          value: session?.defaultLanguage ?? DEFAULT_LANGUAGE,
          icon: null,
          options: LANGUAGE_OPTIONS.map((o) => ({ ...o })),
          helper:
            "Default participant language, shown to every player unless you change their row below. AI narration is translated per player; scoring stays in English.",
          validations: [],
          comments: "",
        },
      ],
    })
  }

  // Spectator Groups / Fields
  // Observers are OPTIONAL. With the bespoke roster always showing one persistent
  // editable row, name/email must NOT be hard-required (an untouched blank
  // observer row would otherwise block Continue). Format/safety validations still
  // apply once a value is entered, and the launch payload filters incomplete rows
  // (spec.name && spec.email). This matches the prior "empty observers = valid".
  const spectatorFields = []
  spectatorFields.push({
    id: `game-detail-${++game_detail}`,
    width: 3,
    type: FIELD_TYPES.STRING,
    label: "Name",
    name: "name",
    placeholder: `Name`,
    value: "",
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.SAFENAME,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
      },
      {
        type: VALIDATION_TYPES.MAX_LENGTH,
        value: validNameFieldMaxLength,
        error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
      },
    ],
    comments: "",
  })
  spectatorFields.push({
    id: `game-detail-${++game_detail}`,
    width: 3,
    type: FIELD_TYPES.EMAIL,
    label: "Email",
    name: "email",
    placeholder: `email@company.com`,
    value: "",
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.EMAIL,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
      },
    ],
    comments: "",
  })
  // Role — free-text column for observers, consistent with the players' role field.
  // Empty + editable, optional. Persisted on spectators[].role via the spread in
  // setupParametersForScenarioFromGameDetailsValues (formSpectators.map({ ...spec })),
  // so it survives to the session doc for both group and collaborative observers.
  // Narrower for non-group so the trailing "Enable Chat" toggle still fits in 12 cols
  // (name 4 + email 4 + role 2 + chat 2); wider (3) for group where there is no chat.
  spectatorFields.push({
    id: `game-detail-${++game_detail}`,
    width: groupGameSetup ? 3 : 2,
    type: FIELD_TYPES.ROLE_INPUT,
    label: "Role",
    name: "role",
    placeholder: "Role (optional)",
    value: "",
    icon: null,
    // Observers aren't scored — the Role is a display label; offer the scenario roles as type-ahead
    // (freeSolo) for visual consistency with the players' Role field. No "All roles" (no scoping).
    suggestions: scenarioRoles ?? [],
    validations: [],
    comments: "",
  })
  // Language — per-observer display language. Observers watch the live exercise, so their
  // narration is translated to this language too (server participantLanguageForEmail resolves
  // spectators[].language). Seeded to the concrete exercise default (English by default) so
  // the dropdown always shows a real language. Same ROW_LANGUAGE_OPTIONS as players. Only
  // rendered for MULTI_LANGUAGE-entitled orgs.
  if (multiLanguageEnabled) {
    spectatorFields.push({
      id: `game-detail-${++game_detail}`,
      width: 2,
      type: FIELD_TYPES.DROPDOWN,
      label: "Language",
      name: "language",
      placeholder: "",
      value: exerciseDefaultLang,
      icon: null,
      options: ROW_LANGUAGE_OPTIONS.map((o) => ({ ...o })),
      validations: [],
      comments: "",
    })
  }
  // Enable Chat — kept per the authoritative mock (Observers = Name / Email /
  // Role / Enable Chat). Wired to canChat exactly as originally. The old "Job
  // Title" column is dropped. Rendered for non-group setups (matches original guard).
  if (!groupGameSetup) {
    spectatorFields.push({
      id: `game-detail-${++game_detail}`,
      width: 2,
      type: FIELD_TYPES.DROPDOWN,
      label: "Enable Chat",
      name: "canChat",
      placeholder: "",
      value: "No",
      icon: null,
      options: [
        { value: "No", display: "No" },
        { value: "Yes", display: "Yes" },
      ],
      // Render as a single radio toggle in the bespoke roster (mock's Enable Chat
      // circle) while still storing the "Yes"/"No" string the launch payload
      // expects (spectators[].canChat → booleanFromString).
      toggleValues: { on: "Yes", off: "No" },
      validations: [],
      comments: "",
    })

    if (isMultiplayer) {
      // Multiplayer Settings group intentionally GONE (product direction 2026-09-05):
      // - Chat is not a choice — a collaborative exercise always has chat.
      // - "Show Other Players' Actions" is not a choice either — actions are always
      //   shared; each player narrows their own view with the in-gameplay
      //   "Viewing" filter instead. The submit path forces both flags to true.

      // Increment view_order for player list
      view_order += 10

      // Add Multi-Player List
      const multiplayerFields = []

      // "Played by" — Human or an autoplay AI teammate. Managers only (autoplay bots
      // exist in the library). A bot row needs no human identity, so it relaxes
      // Name/Email via `activeWhen` and maps to roleBots on submit. The AI teammate is
      // NOT bound to a turn-owning role — the backend runs any enabled autoplay bot in
      // roleBots through the shared gameplay pipeline (see botUtils.runAutoplayTurns).
      if (allowRoleBotAssignment) {
        // "Played by" is a person⇄bot icon switch (no text label). Off = a real person
        // (value "human"); flip on ⇒ this seat is an AI teammate and the bot picker below
        // selects which one. Options are the assignable autoplay bots only — "human" is the
        // switch-off state, not a listed option. Stored value: "human" | <botKey>.
        multiplayerFields.push({
          id: `game-detail-${++game_detail}`,
          width: 2,
          type: FIELD_TYPES.PERSON_BOT_SWITCH,
          label: "Played by",
          name: "playAs",
          value: "human",
          icon: null,
          // Each option carries the bot's `role` so selecting it can auto-fill the Role cell
          // (roleField). Role display is the bot name minus the "(AI)" suffix, e.g. "SOC Lead".
          options: assignableRoleBots.map((p) => {
            const clean = (p.name || p.key).replace(/\s*\(AI\)\s*$/i, "").trim()
            return { value: p.key, display: `${clean} (AI)`, role: clean }
          }),
          roleField: "role",
          validations: [],
          comments: "",
        })
      }
      const playerRowBotAware = allowRoleBotAssignment ? { activeWhen: { field: "playAs", is: "human" } } : {}

      multiplayerFields.push({
        id: `game-detail-${++game_detail}`,
        width: 3,
        type: FIELD_TYPES.STRING,
        label: "Name",
        name: "name",
        placeholder: "Name",
        value: "",
        icon: null,
        options: [],
        ...playerRowBotAware,
        // When this seat is a bot, the Name cell hosts the bot picker (a bot needs no name),
        // so the "Played by" column can stay switch-width. See RosterTable.
        ...(allowRoleBotAssignment ? { botPickerHost: true } : {}),
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
          },
          {
            type: VALIDATION_TYPES.SAFENAME,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
          },
          {
            type: VALIDATION_TYPES.MAX_LENGTH,
            value: validNameFieldMaxLength,
            error_message: errorMessageReplacer(
              VALIDATION_TYPES.MAX_LENGTH,
              LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH,
              validNameFieldMaxLength
            ),
          },
        ],
        comments: "",
      })

      multiplayerFields.push({
        id: `game-detail-${++game_detail}`,
        width: 3,
        type: FIELD_TYPES.EMAIL,
        label: "Email",
        name: "email",
        placeholder: "email@company.com",
        value: "",
        icon: null,
        options: [],
        ...playerRowBotAware,
        validations: [
          {
            type: VALIDATION_TYPES.REQUIRED,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
          },
          {
            type: VALIDATION_TYPES.EMAIL,
            error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
          },
          {
            type: VALIDATION_TYPES.UNIQUE,
            error_message: "Duplicate email addresses are not allowed",
          },
        ],
        comments: "",
      })

      // Role — a MULTI-select dropdown of the scenario's seats for EVERY scenario (user direction
      // 2026-09-16: the dropdown + optional Title must exist in all modes). A player is matched to
      // real seat(s) reliably (the old free-text column mis-seated a player on any typo/casing/
      // rename) and one person can cover MORE than one seat. Nothing checked ("All roles" — consistent with Solo/Independent Group) =
      // a participant without a named seat (still joins as a player). A single-role scenario simply
      // offers its one seat.
      // Free-text Role, consistent with Solo / Independent Group (user direction 2026-09-18): the seat
      // is TYPED (scenario roles + "All roles" as type-ahead) and resolved server-side to a canonical
      // scenario role — title/abbreviation tolerant ("Security" → "SOC Team"), which also removes the
      // old typo/casing/rename mis-seating. Blank or "All roles" = a participant without a named seat
      // (still joins). Resolution is shown in the bottom "Scored on" summary, not inline.
      if (!isCtf)
        multiplayerFields.push({
          id: `game-detail-${++game_detail}`,
          width: 3,
          type: FIELD_TYPES.ROLE_INPUT,
          label: "Role",
          name: "role",
          placeholder: `Role or title — or "${ALL_ROLES_LABEL}"`,
          value: "",
          icon: null,
          suggestions: [ALL_ROLES_LABEL, ...roleSuggestions],
          // Collaborative sub-teams: the roster shows which sub-team each row lands in (read-only tag),
          // resolving the typed role against the scenario seats the same way the launch mapper does.
          subTeamSeats: scenarioRoles,
          validations: [],
          comments: "",
        })

      // Language — per-player display language. Seeded to the concrete exercise default
      // (English by default) so the dropdown always shows a real language; a different code
      // overrides. Carried on players[].language into the launch payload. Canonical gameplay
      // stays English — this only drives the participant's translated view. Only rendered
      // for MULTI_LANGUAGE-entitled orgs.
      if (multiLanguageEnabled) {
        multiplayerFields.push({
          id: `game-detail-${++game_detail}`,
          width: 2,
          type: FIELD_TYPES.DROPDOWN,
          label: "Language",
          name: "language",
          placeholder: "",
          value: exerciseDefaultLang,
          icon: null,
          options: ROW_LANGUAGE_OPTIONS.map((o) => ({ ...o })),
          validations: [],
          comments: "",
        })
      }

      // (The legacy hidden auto-filled Title field is gone — Title is now the explicit optional
      // column pushed above, next to the Role seat dropdown.)

      multiplayerFields.push({
        id: `game-detail-${++game_detail}`,
        width: 1,
        type: FIELD_TYPES.FACILITATOR_TOGGLE,
        label: "Facilitator",
        name: "isFacilitator",
        placeholder: "",
        value: false,
        icon: null,
        options: [],
        validations: [],
        comments: "",
      })

      // Note: Role is automatically set to null (All Roles) for all additional players

      playersViewOrder = view_order
      layoutGroups.push({
        view_order,
        id: "multiplayer_player_list",
        card: "players",
        type: FIELD_GROUP_TYPES.ROSTER,
        export_enabled: true,
        import_enabled: true,
        label: `Players`,
        hint: isCtf
          ? "Your team plays one shared copy and earns one team score — anyone on the roster can answer; no roles needed. Optionally mark facilitators — they run the session and aren't scored."
          : "Each player gets a different role from the scenario — the invite explains theirs. Optionally mark one or more players as facilitators — they run the session and aren't scored.",
        name: MULTIPLAYER_FORM_FIELDS.PLAYER_LIST,
        // The table opens with ONE blank row (no pre-filled role) — the author fills in who plays,
        // adds rows, or imports a CSV (user direction 2026-09-10; no more one-row-per-scenario-role
        // defaults). CSV import REPLACES the roster, so the blank default row is dropped and only
        // the CSV rows show. Edit/duplicate keeps the saved roster.
        value: rosterValueWithSeed(
          session?.[MULTIPLAYER_FORM_FIELDS.PLAYER_LIST] ?? (isEditingSavedType ? multiplayerRosterFromSession({ session }) : null),
          multiplayerFields
        ),
        fields: multiplayerFields,
        validations: [
          {
            type: VALIDATION_TYPES.MIN_LENGTH,
            value: 1,
            error_message: "At least one player is required for multiplayer exercises",
          },
        ],
      })
      // NOTE: autoplay-bot assignment now lives inline on each role's player row (the
      // "Played by" selector above) — the standalone "AI Teammates" table was removed.
    }
  }
  if (isFacilitatedSetup) {
    // Facilitated roundtable: NO per-role people. Show the seats (read-only info) and
    // collect the facilitator's identity (required — they run the room).
    view_order += 10
    const facilitatedFields = []
    facilitatedFields.push({
      id: `game-detail-${++game_detail}`,
      width: 6,
      type: FIELD_TYPES.STRING,
      label: "Facilitator name",
      name: "_facilitatorName",
      placeholder: "Who runs the room",
      value: session?.facilitator?.name ?? "",
      icon: null,
      options: [],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
      ],
      comments:
        "The facilitator presents the exercise and records the room's decisions per seat. On launch the facilitator receives an email invitation with their link; the room's participants need no accounts or invites of their own.",
    })
    facilitatedFields.push({
      id: `game-detail-${++game_detail}`,
      width: 6,
      type: FIELD_TYPES.EMAIL,
      label: "Facilitator email",
      name: "_facilitatorEmail",
      placeholder: "facilitator@company.com",
      value: session?.facilitator?.email ?? "",
      icon: null,
      options: [],
      validations: [
        {
          type: VALIDATION_TYPES.REQUIRED,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
        },
        {
          type: VALIDATION_TYPES.EMAIL,
          error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
        },
      ],
      comments: "",
    })
    // NOTE (user direction 2026-09-10): the per-seat "participant name/email" rows were removed.
    // Named participants are now added ONCE in the Observers section below (name + email + role →
    // each gets a read-only observer link). When observers exist they ARE the named participants —
    // the exercises list, tooltip, and report show them (matched to their seat by role) instead of
    // the bare `<role>@seat.virtual` seats. With no observers, the virtual role-seats show as before.
    // The facilitator card stands in for the Players table in this mode — anchor
    // Observers directly beneath it (same rule every other exercise type follows).
    playersViewOrder = view_order
    layoutGroups.push({
      view_order,
      id: "facilitated_setup",
      // card "players": the page renders card SECTIONS in fixed order (session card,
      // AI panels, players card, observers) — view_order only sorts WITHIN a card.
      // The facilitator card must live in the players slot to sit below the AI
      // sections and directly above Observers, like the Players table does.
      card: "players",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "Facilitated Roundtable",
      fields: facilitatedFields,
    })
    // Additional (co-equal) facilitators — OPTIONAL. More than one person can co-run the room;
    // each additional facilitator gets their own login + Facilitator View with FULL facilitator
    // privileges (the server treats session.facilitators as a co-equal set). The primary
    // facilitator above is facilitators[0]; these rows are the rest. Blank rows are dropped at
    // launch, and the submit de-dupes by email against the primary. Pre-filled on edit/duplicate
    // from session.facilitators minus whoever is shown in the primary fields above.
    const additionalFacilitatorFields = []
    additionalFacilitatorFields.push({
      id: `game-detail-${++game_detail}`,
      width: 6,
      type: FIELD_TYPES.STRING,
      label: "Name",
      name: "name",
      placeholder: "Co-facilitator name",
      value: "",
      icon: null,
      options: [],
      validations: [
        { type: VALIDATION_TYPES.SAFENAME, error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME },
        {
          type: VALIDATION_TYPES.MAX_LENGTH,
          value: validNameFieldMaxLength,
          error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
        },
      ],
      comments: "",
    })
    additionalFacilitatorFields.push({
      id: `game-detail-${++game_detail}`,
      width: 6,
      type: FIELD_TYPES.EMAIL,
      label: "Email",
      name: "email",
      placeholder: "co-facilitator@company.com",
      value: "",
      icon: null,
      options: [],
      validations: [{ type: VALIDATION_TYPES.EMAIL, error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL }],
      comments: "",
    })
    const primaryFacilitatorEmail = (session?.facilitator?.email ?? session?.facilitators?.[0]?.email ?? "").toLowerCase()
    const additionalFacilitatorRows = (session?.facilitators ?? [])
      .filter((f) => f?.email && f.email.toLowerCase() !== primaryFacilitatorEmail)
      .map((f) => ({ name: f.name ?? "", email: f.email }))
    view_order += 2
    layoutGroups.push({
      view_order,
      id: "additional_facilitators",
      card: "players",
      type: FIELD_GROUP_TYPES.ROSTER,
      export_enabled: false,
      import_enabled: false,
      name: "_additionalFacilitators",
      value: rosterValueWithSeed(additionalFacilitatorRows, additionalFacilitatorFields),
      label: "Additional facilitators",
      labelSuffix: "— optional",
      hint: "Co-facilitators who help run the room. Each gets their own login and full facilitator access (they're invited on launch). Leave blank for a single facilitator.",
      fields: additionalFacilitatorFields,
    })
  }

  // Solo "facilitating for a room?" — when the setup-type toggle is on, the single participant row
  // above IS the primary facilitator (scored participant + Facilitator View + injects); there is no
  // separate primary name/email here (that WAS the confusing duplicate "Player + Facilitator" split;
  // user direction 2026-09-14). Only the co-facilitator roster renders. Shown for the one-person
  // modes (single-role "single" / multi-role "solo"); the Facilitated Roundtable handles its own
  // facilitator fields above.
  const isSoloSetup = setupType === "solo" || setupType === "single"
  if (isSoloSetup && soloIsFacilitator) {
    const soloFac = buildSoloFacilitatorFields({ session, idSeed: "main" })
    view_order += 5
    playersViewOrder = view_order
    layoutGroups.push({ ...soloFac.rosterGroup, view_order })
  }

  // Spectators (Observers) section — render directly beneath the Players table
  // when there is one (group / multiplayer). Falls back to the trailing position
  // for solo / single-role runs that have no players table.
  view_order += 5
  const observersViewOrder = playersViewOrder != null ? playersViewOrder + 1 : view_order
  layoutGroups.push({
    view_order: observersViewOrder,
    id: "spectator_details",
    // Observers render in their own region AFTER the players region (player first,
    // then observers) — see new-game-form-details.jsx. Same shared table styling.
    card: "observers",
    type: FIELD_GROUP_TYPES.ROSTER,
    export_enabled: true,
    import_enabled: true,
    name: "spectators",
    value: rosterValueWithSeed(
      session?.spectators?.map((spec) => ({ ...spec, canChat: spec.canChat === true ? "Yes" : "No" })),
      spectatorFields
    ),
    label: "Observers",
    labelSuffix: "— optional",
    hint: "Observers watch the exercise live but can't act — auditors, executives, trainers. Enable chat to let an observer message the players.",
    fields: spectatorFields,
  })
  // Setup Questions Fields
  const setupQuestionsFields = (scenario?.variables ?? []).map((scenarioVariable) => ({
    id: `game-detail-${++game_detail}`,
    width: 12,
    type: layoutFieldTypeForVariableType(scenarioVariable.type),
    label: scenarioVariable.setupQuestion,
    name: scenarioVariable.name,
    placeholder: layoutFieldPlaceholderForVariable({
      variable: scenarioVariable,
    }),
    value: layoutFieldValueForVariable({
      variable: scenarioVariable,
      rawVal: session?.variables?.[scenarioVariable.name]?.value ?? "",
    }),
    icon: null,
    options: fieldOptionsForScenarioVariable(scenarioVariable),
    validations: layoutFieldValidationsForVariable(scenarioVariable),
    comments: "",
  }))
  // Setup Questions Group
  if (setupQuestionsFields.length > 0) {
    view_order += 10
    const setupQuestionsGroup = {
      view_order,
      id: "scenario_variables",
      card: "players",
      type: FIELD_GROUP_TYPES.FIELDS,
      label: "Scenario setup questions",
      fields: setupQuestionsFields,
    }
    layoutGroups.push(setupQuestionsGroup)
  }
  return {
    layout: "1-COLUMN",
    layout_data: [
      {
        id: "layout-1",
        layout_order: 10,
        layout_width: 12,
        groups: layoutGroups,
      },
    ],
  }
}

// The run formats an exercise stores (session.runFormat; groups are told apart by their member sessions).
export const RUN_FORMATS = ["solo", "single", "multiplayer", "facilitated"]

// EDIT round-trip: the run-format (setupType) is NOT persisted — the create/duplicate format picker
// is the only thing that sets it, so on EDIT the form defaulted to "group" and re-rendered every
// exercise as an empty multi-role roster. Infer the setupType (+ soloIsFacilitator) from the SAVED
// session so the edit form restores the right layout. Inverse of the forward mode logic in
// setupParametersForScenarioFromGameDetailsValues. Signals (in precedence order):
//   1. virtual seats  -> Facilitated Roundtable ("facilitated")
//   2. group exercise -> "independentGroup" (multi-role) / "group" (single-role)
//   3. >1 real person -> Collaborative ("multiplayer") — on a single-role scenario too (its picker
//      stores "multiplayer"; reading it back as a one-person run dropped everyone but one person).
//      One person whose AI teammates play the other roles (otherRolesMode "autoplay" — a Solo only
//      ever stores "skip" or nothing) is Collaborative too.
//   4. otherwise a one-person run -> "solo" (multi-role) / "single" (single-role);
//      facilitator(s) present on a one-person run = solo-facilitating-a-room (soloIsFacilitator).
export const classifySetupTypeFromSession = ({ session, scenario }) => {
  const scenarioRoles = rolesDefinedByScenario(scenario)
  const isMultiRole = (scenarioRoles?.length ?? 0) > 1
  const players = Array.isArray(session?.players) ? session.players : []
  const realPlayers = players.filter((p) => p?.virtual !== true)
  const hasVirtualSeats = players.some((p) => p?.virtual === true)
  const facilitators = Array.isArray(session?.facilitators) ? session.facilitators : session?.facilitator ? [session.facilitator] : []
  // A group exercise loads as a group with member sessions (session.sessions) and/or a group-name
  // marker.
  const isGroup = !!(
    session?.groupName ||
    session?.gameGroupName ||
    session?.groupGameName ||
    (Array.isArray(session?.sessions) && session.sessions.length > 0)
  )

  if (hasVirtualSeats) return { setupType: "facilitated", soloIsFacilitator: false }
  if (isGroup) return { setupType: isMultiRole ? "independentGroup" : "group", soloIsFacilitator: false }
  // The format the author chose, stored since 2026-10-29 (runFormat) — exact; older exercises are inferred.
  // A one-person format follows the CURRENT scenario's role count (Restart / Edit can change the scenario):
  // "single" on a multi-role scenario would pin the person to the first role.
  if (RUN_FORMATS.includes(session?.runFormat)) {
    const isOnePerson = session.runFormat === "solo" || session.runFormat === "single"
    const setupType = isOnePerson ? (isMultiRole ? "solo" : "single") : session.runFormat
    return { setupType, soloIsFacilitator: isOnePerson && facilitators.length > 0 }
  }
  if (realPlayers.length > 1 || (realPlayers.length === 1 && session?.otherRolesMode === "autoplay")) {
    return { setupType: "multiplayer", soloIsFacilitator: facilitators.length > 0 }
  }
  return { setupType: isMultiRole ? "solo" : "single", soloIsFacilitator: facilitators.length > 0 }
}

// Edit / Restart: the run format (_isGroupGameSetup) isn't written to the details form there (the picker
// is hidden), so the launch payload was built without it — a Solo exercise scored on one role was saved
// back as whole-incident with the player moved to the anchor role, and the Collaborative-only steps
// never ran. Restore it from the saved exercise (as the details step does for its layout). Only the
// one-exercise formats: group and facilitated edits keep their existing path.
export const detailsWithRestoredSetupType = ({ details, isEditing, session, scenario }) => {
  if (!isEditing || !session || details?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]) return details
  const restore = (setupType) => ({ ...details, [GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]: setupType })
  const { setupType } = classifySetupTypeFromSession({ session, scenario })
  // Stored with the exercise (since 2026-10-29): exact.
  if (RUN_FORMATS.includes(session?.runFormat)) return ["solo", "single", "multiplayer"].includes(setupType) ? restore(setupType) : details
  // Older exercises: only when the saved shape can't be anything else. Several people (or one person
  // whose AI teammates play the other roles) is a team; one person holding EVERY role of a multi-role
  // scenario is a whole-incident Solo. A one-person exercise scoped to some roles saves the same shape
  // as a one-person team, so it keeps its previous behaviour (no format sent).
  if (setupType === "multiplayer") return restore("multiplayer")
  if (setupType === "solo") {
    const realPlayers = (Array.isArray(session?.players) ? session.players : []).filter((p) => p?.virtual !== true)
    const email = String(realPlayers[0]?.email ?? "").toLowerCase()
    const roles = rolesDefinedByScenario(scenario) ?? []
    const assigned = session?.roleAssignments ?? {}
    if (realPlayers.length === 1 && email && roles.length > 1 && roles.every((r) => String(assigned[r] ?? "").toLowerCase() === email)) {
      return restore("solo")
    }
  }
  return details
}

const isGameGroupFromDetailsValues = ({ scenario, values }) => {
  // Determine if the current setup should be treated as a "group game"
  // 1) Preferred: explicit setup type === "group" (single-role) or "independentGroup" (multi-role:
  //    each person runs the scenario independently, scored separately).
  const setupTypeSelection = values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]
  if (setupTypeSelection === "group" || setupTypeSelection === "independentGroup") {
    return true
  }
  // Explicit NON-group selections short-circuit BEFORE the backward-compat roster inference below —
  // otherwise a stale `_groupPlayers` roster (left over from switching away from Independent Group)
  // would make a Solo / single-role Solo / Collaborative / Facilitated launch fall back to that group
  // roster. These setup types are persisted into details_form on submit, so the marker is reliable.
  if (
    setupTypeSelection === "solo" ||
    setupTypeSelection === "single" ||
    setupTypeSelection === "multiplayer" ||
    setupTypeSelection === "facilitated"
  ) {
    return false
  }

  // 2) Backward compatibility: infer from group players list + single-role scenario
  const scenarioRoles = rolesDefinedByScenario(scenario)
  const isSingleRoleScenario = (scenarioRoles?.length ?? 0) === 1

  const groupPlayers = values?.[GROUP_GAME_FORM_FIELDS.PLAYER_LIST] ?? []
  if (Array.isArray(groupPlayers) && groupPlayers.length > 0 && isSingleRoleScenario) {
    return true
  }

  return false
}

const groupGamePlayersFrom = ({ values }) => {
  return values?.[GROUP_GAME_FORM_FIELDS.PLAYER_LIST] ?? null
}

const multiplayerPlayersFrom = ({ values }) => {
  return values?.[MULTIPLAYER_FORM_FIELDS.PLAYER_LIST] ?? null
}

const groupGameBatchSizeFrom = ({ values }) => {
  const batchSizeRaw = values?.[GROUP_GAME_FORM_FIELDS.BATCH_SIZE] ?? "50"
  return parseInt(batchSizeRaw)
}

const groupGameBatchTimeIntervalSecondsFrom = ({ values }) => {
  const batchIntervalRaw = values?.[GROUP_GAME_FORM_FIELDS.BATCH_START_INTERVAL_MINUTES] ?? "0"
  return parseInt(batchIntervalRaw) * 60 // convert minutes to seconds
}

const gameNLPAutoDisableThresholdFrom = ({ values }) => {
  const rawVal = values?.[GAME_SETUP_TYPE_FORM_FIELDS.NLP_AUTO_DISABLE_THRESHOLD] ?? "0"
  return parseInt(rawVal)
}

const gameNLPModeFrom = ({ values }) => {
  const rawVal = values?.[GAME_SETUP_TYPE_FORM_FIELDS.NLP_MODE_BOOLEAN] ?? "true"
  return rawVal === "true" ? true : false
}

const nlpContextFrom = ({ values }) => {
  // Check for settings
  let nlpModelPromptMatchedActionsPrescreen = values?.[GAME_SETUP_TYPE_FORM_FIELDS.NLP_MODEL_PROMPT_MATCHED_ACTIONS_PRESCREEN] ?? ""
  if (nlpModelPromptMatchedActionsPrescreen) {
    return {
      nlpModelPromptMatchedActionsPrescreen,
    }
  }

  // Default is 'null' which implies default values
  return null
}

// Longest role/assignee title the server accepts (gameplay validation.max_name_field_length).
// Checked BEFORE submitting a launch so the user gets a pointed message naming the role,
// instead of a raw server validation error keyed by a (possibly very long) map path.
export const MAX_ROLE_TITLE_LENGTH = 1024

// Returns a user-facing problem message when any role's assignee title would be rejected by
// the server's length validation, else null. Titles default to the scenario role name itself
// (see the fallbacks in this module), so an over-long generated role name is the usual culprit.
export const roleTitleLengthProblem = ({ roleAssigneeTitles }) => {
  for (const [role, title] of Object.entries(roleAssigneeTitles ?? {})) {
    const titleLength = String(title ?? "").length
    if (titleLength > MAX_ROLE_TITLE_LENGTH) {
      const preview = role.length > 60 ? `${role.slice(0, 60)}…` : role
      return (
        `The title for role '${preview}' is ${titleLength} characters — the maximum is ${MAX_ROLE_TITLE_LENGTH}. ` +
        `The title defaults to the scenario's role name, so shorten that role in the scenario (or enter a shorter participant title here).`
      )
    }
  }
  return null
}

// Collaborative role-scoped scoring: given the scenario's seats, the human-staffed seats, and the
// FINAL roleBots map (roster bots + AI Stakeholders), decide how the UNSTAFFED seats behave.
// - no staffed seats, or humans staff every seat → whole incident, no override (null).
// - autoplay ONLY when EVERY unstaffed seat has a bot — otherwise a bot-less unstaffed seat would
//   strand an autoplay walk; in that case skip the unstaffed seats instead. Synthetic seatless bot
//   keys ("AI Teammate N") aren't scenario roles, so they never count toward coverage.
// Must be called with the FINAL roleBots (after AI Stakeholders are merged) to be accurate.
export const resolveCollaborativeOtherRolesMode = ({ scenarioRoles, humanSeats, roleBots } = {}) => {
  const seats = Array.isArray(scenarioRoles) ? scenarioRoles : []
  const staffed = Array.isArray(humanSeats) ? humanSeats : []
  if (staffed.length === 0) return null
  const coversAll = seats.length > 0 && seats.every((r) => staffed.includes(r))
  if (coversAll) return null
  const unstaffed = seats.filter((r) => !staffed.includes(r))
  // Match a seat to a bot CASE-INSENSITIVELY against roleBots' OWN keys. roleBots keys can be
  // non-canonical: an AI Stakeholder entered as "soc lead" for the seat "SOC Lead" must still read as
  // bot-covered, or the seat is wrongly stamped "skip" and the stakeholder never autoplays. Object.keys
  // returns only own enumerable keys, so a seat named like an Object.prototype member ("constructor",
  // "toString", "__proto__") never falsely reads as covered.
  const botKeys = roleBots && typeof roleBots === "object" ? Object.keys(roleBots) : []
  const hasBot = (r) => botKeys.some((k) => k.toLowerCase() === String(r).toLowerCase() && !!roleBots[k])
  const allUnstaffedBotCovered = unstaffed.length > 0 && unstaffed.every((r) => hasBot(r))
  return allUnstaffedBotCovered ? "autoplay" : "skip"
}

export const setupParametersForScenarioFromGameDetailsValues = ({ scenario, values }) => {
  // Fixed Game Setup
  const orgId = values?._orgId

  // Exercise-level default language (seeds roster rows; also stored on the session). For solo it is
  // the player's language. Normalized to the en/fr/fi allowlist. Declared HERE (top of the function)
  // because the spectators/players .map() callbacks below read it — a later declaration put it in a
  // temporal-dead-zone that threw "Cannot access 'defaultLanguage' before initialization" the moment
  // an exercise had ≥1 observer (the spectators map ran). Only depends on `values`.
  // SOLO / single-role SOLO has no top-level Language field — the language lives ONLY on the single
  // player row (the facilitator block emits just name/email, no language), so the row value is
  // authoritative here. Every other format uses the top-level field. See the precedence just below.
  const soloLangSetupType = values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]
  const soloLangAnchorRole = soloLangSetupType === "solo" || soloLangSetupType === "single" ? rolesDefinedByScenario(scenario)?.[0] : null
  const soloRowLanguage = soloLangAnchorRole ? values?.[paramNameForRole({ role: soloLangAnchorRole, param: "language" })] : null
  // For Solo / single-role Solo the top-level Language field is HIDDEN, so the visible per-row language
  // must WIN — a stale top-level `_defaultLanguage` left over from a Collaborative/Independent-Group
  // selection must not override the row (e.g. picking French on the row then submitting English). The
  // row falls back to the (hidden) top-level only when it's empty. Every other format keeps top-level
  // precedence as before.
  const defaultLanguage = normalizeLanguage(
    soloLangAnchorRole
      ? soloRowLanguage || values?.[MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE]
      : values?.[MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE] || soloRowLanguage
  )

  // "Solo — your role" / role-scoped Independent Group on a MULTI-role scenario. Whole-scenario
  // scope leaves soloRoles null (reuses the no-fixed-role engine, whole-incident score); one-role
  // scope pins the chosen role + how the OTHER roles are handled. Only set when the user explicitly
  // picked Solo/Independent Group on a multi-role scenario — every other flow keeps null.
  const soloSetupType = values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]
  const soloScenarioRoles = rolesDefinedByScenario(scenario)
  // SOLO: the single player's role comes from their roster row's Role selector (All roles / a
  // specific scenario role / Facilitator) — not a top-level field. The row binds to the first
  // scenario role (anchor); read its playerRole to decide scoping. A specific scenario role →
  // scored on that role; All roles ("") or Facilitator → whole incident (soloRoles null). A stale
  // value is naturally ignored because only an exact scenario-role match pins soloRoles.
  const soloAnchorRole = soloScenarioRoles?.[0]
  // Both one-person modes render the free-text Role field (multi-role "solo" AND single-role
  // "single" — a scenario whose roles are stored as one composite cell is "single"), so read it for
  // both. Reading it only for "solo" silently dropped the typed role (e.g. "SOC Lead") on launch.
  const isOnePersonSetup = soloSetupType === "solo" || soloSetupType === "single"
  const soloPlayerRoleValue = isOnePersonSetup && soloAnchorRole ? values?.[paramNameForRole({ role: soloAnchorRole, param: "playerRole" })] : null
  // FREE-TEXT role: the field is a string the user typed (a role, a title, an abbreviation). Send it
  // through verbatim as soloRoles — the SERVER resolves it to a scenario role (exact → normalized →
  // synonym → embedding). Blank or "All roles" → [] = whole incident. Whole-incident is an EMPTY ARRAY
  // (not null) so editing a role-scoped session back to "All roles" EXPLICITLY clears the stored scope
  // (updateSession only writes soloRoles when it receives an array; [] and null both == whole incident
  // on the server). A legacy array value is tolerated by the helper.
  const soloRoles = isOnePersonSetup ? soloRolesFromRoleFieldValue(soloPlayerRoleValue) : []
  // The typed role is also the person's display title (roster, Facilitator View, report) — the
  // Title column was removed when Role became free-text. Blank / "All roles" → the scenario role.
  const soloTypedTitle = soloRoles.join(", ")
  // Independent Group carries per-member soloRoles on EACH player row (see playerList below); its
  // session/group-level soloRoles stays [] so the server uses per-member scoping, not one shared value.
  const otherRolesMode = soloRoles.length > 0 ? "skip" : null

  // Name
  const name = values?._name || defaultGameName()

  // Scheduled Start Time
  // "start_date": "13-06-2023 4:46 pm",
  // "time_zone": "EST",
  const startDate = values?.start_date
  // const startTZ = values?.time_zone
  let scheduledTime = null

  if (startDate) {
    const composedDispatchTimeFormat = `${DATE_FORMAT} ${TIME_FORMAT}`
    const composedDispatchTimeString = startDate
    // Note: we are guessing the user's current timezone (for now)
    const composedDate = moment.tz(composedDispatchTimeString, composedDispatchTimeFormat, moment.tz.guess(true))
    const dispatchDate = composedDate.toDate()
    scheduledTime = `${dispatchDate.getTime()}`
    pageLogger.info(`Got dispatchTime of :${composedDate}`)
  }

  // NLP Settings
  const nlpContext = nlpContextFrom({ values })
  const nlpEnabled = gameNLPModeFrom({ values })
  const nlpMatchThreshold = 0.45
  const nlpModel = values?._nlpModel ?? defaultSessionNLPModel
  const nlpAutoDisableThreshold = gameNLPAutoDisableThresholdFrom({ values })

  // Group Game Setup?
  let playerList = null
  let batchSize = null
  let scheduledTimeForFirstBatch = null
  let timeIntervalBetweenBatches = null // Seconds
  const isGroupGameSetup = isGameGroupFromDetailsValues({
    scenario,
    values,
  })
  if (isGroupGameSetup) {
    batchSize = groupGameBatchSizeFrom({ values })
    scheduledTimeForFirstBatch = Math.floor((scheduledTime ? parseInt(scheduledTime) : new Date().getTime()) / 1000) // Default to now (seconds since epoch time)
    timeIntervalBetweenBatches = groupGameBatchTimeIntervalSecondsFrom({
      values,
    })
    playerList = groupGamePlayersFrom({ values })
    // Resolve each group roster row's language: a concrete row value wins, else the row
    // falls through to the exercise default. `|| default` (not ??) so the "" ("Exercise
    // default") option correctly inherits. The server write spreads the row, so
    // `language` rides along.
    if (Array.isArray(playerList)) {
      const groupDefaultLanguage = values?.[MULTIPLAYER_FORM_FIELDS.DEFAULT_LANGUAGE]
      // Independent Group: each person TYPES their OWN Role in their row (a scenario role, a title, or
      // "All roles") via the free-text ROLE_INPUT — rendered for EVERY scenario. The typed value(s) ride
      // through verbatim as the per-member `soloRoles` the server fan-out stamps; the SERVER resolves
      // each to a scenario role (title/abbreviation tolerant) or falls back to whole incident. The
      // dedicated `title` column carries the display label. (user direction 2026-09-18.)
      const isIndependentGroup = soloSetupType === "independentGroup" || soloSetupType === "group"
      playerList = playerList.map((p) => {
        const base = { ...p, language: normalizeLanguage(p?.language || groupDefaultLanguage) }
        if (!isIndependentGroup) return base
        // Free-text Role → soloRoles: the typed value(s) verbatim; blank / "All roles" → [] (whole
        // incident for this member — an empty array, not null, so an edit clears any prior scope).
        // The server resolves each typed role; it never silently drops.
        const memberSoloRoles = soloRolesFromRoleFieldValue(p?.role)
        // players[].role stays a SCALAR (legacy contract — NewReport reads `player.title || player.role`):
        // the sole scoped role, else null. Never emit an array/[] here (that would render as the label).
        const scalarRole = memberSoloRoles.length === 1 ? memberSoloRoles[0] : null
        // Report label: a custom Title wins (TRIMMED — a whitespace-only title must not stay truthy
        // and blank out the role label); a ROLE-SCOPED multi-seat member with no title falls back to
        // their joined role names (else the report shows them with NO role). An explicit all-roles
        // member (memberSoloRoles collapsed to []) and single-role members keep title empty so the
        // report's `player.title || player.role` falls through to the role.
        const trimmedTitle = typeof base.title === "string" ? base.title.trim() : base.title
        const memberTitle = trimmedTitle || (memberSoloRoles.length > 1 ? memberSoloRoles.join(", ") : trimmedTitle)
        return { ...base, soloRoles: memberSoloRoles, role: scalarRole, title: memberTitle }
      })
    }
  }

  //Role Assignments
  const roleAssignments = {}
  const roleAssigneeNames = {}
  const roleAssigneeTitles = {}
  // Per-role display language (en/fr/fi) for solo / single-role runs. Carried onto
  // players[].language; canonical gameplay content stays English.
  const roleAssigneeLanguages = {}
  const scenarioRoles = rolesDefinedByScenario(scenario)

  // Resolve a Collaborative roster row's free-text Role (a ROLE_INPUT STRING) to the scenario's
  // CANONICAL seats (title/abbreviation tolerant: "Head of SOC" → "SOC Lead"). Seat ASSIGNMENT,
  // role-scoped SCORING (params.soloRoles), and BOT-seat mapping MUST all resolve the same way — else a
  // seat that IS assigned gets dropped from scoring (its turns skipped) or a bot never covers the
  // canonical seat. Deterministic tiers only (the server adds the semantic LLM tier for scoring; seat
  // matching uses the same tiers as the live "Scored on" preview). Blank/"All roles"/unmatched → [].
  // The Roles check (review step) confirmed each person's scenario role: that choice decides the seat
  // (null = not in this scenario -> no seat). Without a confirmed choice (older callers), only a strict
  // match counts — never the synonym tier (design 2026-10-29 §4).
  const confirmedScoringRoles = values?._confirmedScoringRoles ?? null
  const resolveRowSeats = (roleVal, email = null) => {
    const key = String(email ?? "")
      .trim()
      .toLowerCase()
    if (confirmedScoringRoles && key && Object.prototype.hasOwnProperty.call(confirmedScoringRoles, key)) {
      const confirmed = confirmedScoringRoles[key]
      return Array.isArray(confirmed) ? confirmed : confirmed ? [confirmed] : []
    }
    const typed = soloRolesFromRoleFieldValue(roleVal)
    // Typed roles map against the full vocabulary (listed + stored roles), like the server.
    return typed.length
      ? resolveSoloRolesSync({ requestedRoles: typed, scenarioRoles: scenarioRoleCandidates(scenario), strict: true, initials: true }).resolved
      : []
  }

  // Autoplay-bot role assignments ({ scenarioRole: botKey }) are chosen inline on each
  // role's player row via the per-role "Played by" selector; collected in the role loop below.
  const roleBots = {}

  // Get multiplayer players list (humans only — bots are staffed via the AI Teammates table).
  const formPlayers = values?._multiplayerPlayers ?? []
  // Facilitated roundtable: the facilitator runs the room; every scenario role becomes a
  // VIRTUAL SEAT (reserved-domain email, no invites) the facilitator records decisions for.
  // Facilitated (virtual seats, no scored player) = the Facilitated Roundtable format ONLY. Solo +
  // "facilitating for a room? = Yes" is NOT this — it's a normal scored solo player PLUS facilitator(s)
  // who watch the live board and push injects (facilitators ride in the facilitators array, not seats).
  const isFacilitated = values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === "facilitated"
  // A roundtable seats EVERY scenario role, including roles that own no actions.
  const facilitatedSeatRoles = isFacilitated ? facilitatedSeatRolesForScenario(scenario) : []
  // Detect multiplayer mode: explicitly set, inferred from players, or facilitated (which is
  // a multi-seat session run by one person — multiplayer semantics, virtual identities).
  // SOLO takes PRECEDENCE over a stale _multiplayerPlayers roster: switching Collaborative→Solo
  // leaves the old collaborative roster in the form values, and a bare `formPlayers.length > 0`
  // check would wrongly treat the Solo submit as multiplayer and pick the stale roster's first
  // email instead of the entered Solo row. When the setup type is explicitly a one-person mode —
  // "solo" (multi-role) or "single" (single-role) — never infer multiplayer from the leftover
  // roster. (Solo-facilitating-a-room is NOT an exception: it stays a scored one-person session; the
  // facilitator rides in facilitators[] and does not make the session multiplayer.)
  const soloSetupBoolean = values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN]
  const isSoloSetup = soloSetupBoolean === "solo" || soloSetupBoolean === "single"
  const isMultiplayer =
    isFacilitated || (!isSoloSetup && (values?.[GAME_SETUP_TYPE_FORM_FIELDS.SETUP_GROUP_BOOLEAN] === "multiplayer" || formPlayers.length > 0))

  // Multiplayer seats from the roster. Single-role: the first player fronts every role
  // (everyone shares the incident — today's behavior). Multi-role: each row's Role column is a
  // MULTI-select of the scenario seats that row's person fills — so ONE person can cover several
  // seats (their email is assigned to each picked role). First row that lists a given seat wins it;
  // rows that pick nothing ("All roles") still join as players[] — participants without a seat.
  if (isMultiplayer && formPlayers.length > 0) {
    const normRole = (r) =>
      String(r ?? "")
        .trim()
        .toLowerCase()
    // A row's Role is free-text (a role, title, abbreviation; possibly a legacy array or comma-joined
    // string). Resolve it to the scenario's CANONICAL roles (title/abbreviation tolerant: "Security" →
    // "SOC Team") so seat matching works on what the person typed, not the literal string. Blank /
    // "All roles" / unmatched → [] (joins without a named seat). Deterministic tiers only (the server
    // adds the semantic LLM tier for scoring; seat assignment uses the same tiers as the live preview).
    const rowRoles = (pl) => resolveRowSeats(pl?.role, pl?.email)
    // Seat matching runs whenever ANY row resolved to a seat. Legacy rosters with no picks keep the old
    // behavior: on a single-role scenario the FIRST player fronts the role.
    const anyExplicitSeatPick = formPlayers.some((pl) => rowRoles(pl).length > 0)
    if (scenarioRoles.length <= 1 && !anyExplicitSeatPick) {
      const firstPlayer = formPlayers[0]
      if (firstPlayer.email && firstPlayer.name) {
        for (const scenarioRole of scenarioRoles) {
          roleAssignments[scenarioRole] = firstPlayer.email.toLowerCase()
          roleAssigneeNames[scenarioRole] = firstPlayer.name
          roleAssigneeTitles[scenarioRole] = firstPlayer.title || firstPlayer.name
        }
      }
    } else {
      for (const scenarioRole of scenarioRoles) {
        const row = formPlayers.find((pl) => pl?.email && rowRoles(pl).some((r) => normRole(r) === normRole(scenarioRole)))
        if (!row) continue
        roleAssignments[scenarioRole] = row.email.toLowerCase()
        roleAssigneeNames[scenarioRole] = row.name || row.email
        // Per-seat display title: the person's explicit optional Title, else the seat name.
        roleAssigneeTitles[scenarioRole] = (typeof row.title === "string" && row.title.trim()) || scenarioRole
        if (typeof row.language === "string" && row.language.trim() !== "") {
          roleAssigneeLanguages[scenarioRole] = normalizeLanguage(row.language)
        }
      }
    }
  }

  // Facilitated roundtable: every scenario role is a virtual seat.
  if (isFacilitated) {
    for (const scenarioRole of facilitatedSeatRoles) {
      roleAssignments[scenarioRole] = virtualSeatEmail(scenarioRole)
      roleAssigneeNames[scenarioRole] = scenarioRole
      roleAssigneeTitles[scenarioRole] = scenarioRole
    }
  }

  // For non-multiplayer games, populate from form values
  if (!isMultiplayer) {
    for (const scenarioRole of scenarioRoles) {
      // Bot or human? The per-role "Played by" selector (multi-role only) is either a bot
      // key or 'human'. A bot-played role maps to roleBots and has no human name/email.
      const playAs = values?.[paramNameForRole({ role: scenarioRole, param: "playAs" })]
      const assigneeTitleParam = paramNameForRole({ role: scenarioRole, param: "title" })
      if (playAs && playAs !== "human") {
        roleBots[scenarioRole] = playAs
        roleAssigneeTitles[scenarioRole] = values?.[assigneeTitleParam] || scenarioRole
        continue
      }
      // Name
      const assigneeNameParam = paramNameForRole({
        role: scenarioRole,
        param: "name",
      })
      roleAssigneeNames[scenarioRole] = values?.[assigneeNameParam] ?? ""
      // Email
      const assigneeEmailParam = paramNameForRole({
        role: scenarioRole,
        param: "email",
      })
      roleAssignments[scenarioRole] = values?.[assigneeEmailParam] ?? undefined
      // Title
      roleAssigneeTitles[scenarioRole] = values?.[assigneeTitleParam] || scenarioRole
      // Language (per-seat display language) — record ONLY when the row set a concrete
      // value. An empty row is left out so the seat inherits the exercise default; this
      // keeps roleAssigneeLanguages a map of genuine per-role overrides.
      const assigneeLanguageParam = paramNameForRole({ role: scenarioRole, param: "language" })
      const rawRoleLanguage = values?.[assigneeLanguageParam]
      if (typeof rawRoleLanguage === "string" && rawRoleLanguage.trim() !== "") {
        roleAssigneeLanguages[scenarioRole] = normalizeLanguage(rawRoleLanguage)
      }
    }

    // SOLO is ONE player entered on a single (anchor) row, but they PLAY either the whole scenario
    // (All roles) or one chosen role. The loop above only assigned the anchor role, so the server —
    // which derives roleSlots from roleAssignments keys — scoped the whole exercise to the anchor
    // role even for "All roles" (bug: 'All roles' showed one role's actions). Reassign the single
    // player across EVERY role they play so roleSlots covers them: all scenario roles for All roles,
    // or just the chosen role. (Independent Group uses its own per-member path, not this.)
    if (soloSetupType === "solo" && soloAnchorRole) {
      const soloEmail = roleAssignments[soloAnchorRole]
      const soloName = roleAssigneeNames[soloAnchorRole]
      const soloTitle = values?.[paramNameForRole({ role: soloAnchorRole, param: "title" })]
      const soloLang = roleAssigneeLanguages[soloAnchorRole]
      // The Roles check's confirmed role decides the seat: a scenario role, or (not in this scenario)
      // every role. Without one, the typed roles as before.
      const soloConfirmedKey = soloEmail ? String(soloEmail).trim().toLowerCase() : ""
      const soloConfirmed =
        confirmedScoringRoles && soloConfirmedKey && Object.prototype.hasOwnProperty.call(confirmedScoringRoles, soloConfirmedKey)
          ? confirmedScoringRoles[soloConfirmedKey]
          : undefined
      const soloConfirmedList = Array.isArray(soloConfirmed)
        ? soloConfirmed
        : soloConfirmed
          ? [soloConfirmed]
          : soloConfirmed === null
            ? []
            : undefined
      const playedRoles =
        soloConfirmedList !== undefined
          ? soloConfirmedList.length
            ? soloConfirmedList
            : scenarioRoles
          : soloRoles && soloRoles.length > 0
            ? soloRoles
            : scenarioRoles
      for (const k of Object.keys(roleAssignments)) delete roleAssignments[k]
      for (const k of Object.keys(roleAssigneeNames)) delete roleAssigneeNames[k]
      for (const k of Object.keys(roleAssigneeTitles)) delete roleAssigneeTitles[k]
      for (const k of Object.keys(roleAssigneeLanguages)) delete roleAssigneeLanguages[k]
      for (const r of playedRoles ?? []) {
        if (soloEmail) roleAssignments[r] = soloEmail
        roleAssigneeNames[r] = soloName ?? ""
        roleAssigneeTitles[r] = soloTitle && soloTitle.trim() ? soloTitle : r
        if (soloLang) roleAssigneeLanguages[r] = soloLang
      }
    }
    // Single-role one-person run: the loop above assigned the anchor role; keep the typed role as
    // its title so the roster and Facilitator View show "SOC Lead", not the scenario role name.
    if (soloSetupType === "single" && soloAnchorRole && soloTypedTitle && !values?.[paramNameForRole({ role: soloAnchorRole, param: "title" })]) {
      roleAssigneeTitles[soloAnchorRole] = soloTypedTitle
    }
  }

  // Spectators
  const formSpectators = values?.spectators ?? []
  const spectators = formSpectators
    .filter((spec) => spec.name && spec.email)
    .map((spec) => {
      // Make sure 'canChat' is boolean; normalize observer language ("" inherits the
      // exercise default, || not ?? so empty falls through) so their narration is translated.
      return {
        ...spec,
        canChat: spec.canChat ? booleanFromString(spec.canChat) : isMultiplayer ? true : false,
        language: normalizeLanguage(spec.language || defaultLanguage),
      }
    })

  // Facilitated: each seat with an optional participant email is ALSO added as an OBSERVER, so that
  // person can follow the live board read-only on their OWN screen (the facilitator still records
  // their seat's decisions). Marked `fromSeat` so edit/duplicate re-seeds the per-seat email field
  // and so they can be told apart from manually-added observers. De-duped against existing observers.
  if (isFacilitated) {
    const seenObserverEmails = new Set(spectators.map((s) => s.email?.toLowerCase()).filter(Boolean))
    for (const scenarioRole of facilitatedSeatRoles) {
      const seatEmail = (values?.[paramNameForRole({ role: scenarioRole, param: "seatEmail" })] || "").trim().toLowerCase()
      if (!seatEmail || seenObserverEmails.has(seatEmail)) continue
      seenObserverEmails.add(seatEmail)
      const seatName = (values?.[paramNameForRole({ role: scenarioRole, param: "seatName" })] || "").trim() || scenarioRole
      spectators.push({
        email: seatEmail,
        name: seatName,
        role: scenarioRole,
        canChat: false, // observers watch read-only; the facilitator drives the room
        canPlay: false,
        fromSeat: true,
        language: normalizeLanguage(defaultLanguage),
      })
    }
  }

  // Setup Questions / Variables
  const variables = {}
  for (const scenarioVar of scenario?.variables ?? []) {
    const name = scenarioVar.name
    const defaultValue = scenarioVar.default
    const type = scenarioVar.type
    const otherVal = values?.[`other_${scenarioVar.name}`]
    let rawVal = values?.[scenarioVar.name]
    let val
    if (Array.isArray(rawVal)) {
      // Swap in the "Other" value (if necessary)
      rawVal = rawVal
        .map((origVal) => {
          return origVal.toLowerCase() === OTHER_OPTION_PLACEHOLDER_VALUE.toLowerCase() ? otherVal : origVal
        })
        .filter((val) => val !== "") // Remove empty values
      rawVal = rawVal.join(",")
      val = rawVal
    } else {
      val = otherVal || rawVal || (defaultValue ?? "")
    }

    variables[name] = {
      type,
      value: val,
    }
  }

  // Facilitator MODE-GATING happens ONCE, at the details submit (submitCallback): it resolves the
  // primary/list (_facilitators, _facilitatorEmail) per mode and CLEARS the co-facilitator roster
  // (_additionalFacilitators) for any mode that doesn't use it. By the time these reach this builder
  // they are already correct for the current mode, so it TRUSTS them and does NOT re-gate. (An earlier
  // re-gate here keyed on `_soloIsFacilitator`, which lives only in the setup-type form and is never
  // persisted into details_form — so it evaluated false for Solo+Yes and silently dropped every
  // co-facilitator.) Merge the primary + the "Additional facilitators" roster + the mode-resolved
  // `_facilitators`; blank rows carry no email and drop out in the de-dupe below.
  const facilitatorEmail = values?._facilitatorEmail ?? null
  const facilitatorName = values?._facilitatorName ?? ""
  const facilitator = facilitatorEmail ? { email: facilitatorEmail.toLowerCase(), name: facilitatorName } : null
  const additionalFacilitators = Array.isArray(values?._additionalFacilitators) ? values._additionalFacilitators : []
  const legacyFacilitators = Array.isArray(values?._facilitators) ? values._facilitators : []
  const facilitatorsRaw = [...(facilitator ? [facilitator] : []), ...additionalFacilitators, ...legacyFacilitators]
  const facilitatorSeen = new Set()
  const facilitators = []
  for (const f of facilitatorsRaw) {
    const email = f?.email?.toLowerCase()
    if (!email || facilitatorSeen.has(email)) continue
    facilitatorSeen.add(email)
    facilitators.push({ email, name: f.name ?? "" })
  }

  // "Show live timeline and facts to all players" is ALWAYS ON (the toggle was removed,
  // user direction 2026-09-10) — force it true for every exercise type.
  const executiveDetailsEnabled = true
  // AI bots selected for this session (manager-only field; undefined when the
  // picker wasn't shown so updateSession leaves any existing selection untouched)
  const sessionBots = Array.isArray(values?.["_sessionBots"]) ? values["_sessionBots"] : undefined
  // CTF Coordinator config — only present when the enable toggle is checked.
  // undefined when the field group wasn't shown so non-CTF sessions are unaffected.
  // Three-way, deliberately: the field group may not have been rendered at all
  // (leave any existing config untouched, like the bots picker), rendered and
  // ticked, or rendered and UNticked. That last case must send `{enabled:false}` —
  // sending undefined would make CTF impossible to turn back off, because
  // arango.updateSession ignores undefined and keeps the stored value.
  const ctfFieldGroupWasShown = !!values && "_ctfEnabled" in values
  const ctfIsEnabled = Array.isArray(values?.["_ctfEnabled"]) && values["_ctfEnabled"].includes("ctf")
  // A CTF scenario's Challenge settings card (details step) wins over the legacy CTF Coordinator fields.
  const ctfExerciseSettings = values?.[CTF_SETTINGS_FIELD]
  const legacyCtfCoordinator = ctfIsEnabled
    ? {
        enabled: true,
        // These are MULTI_OPTIONS arrays, not booleans. Truthiness is wrong here:
        // an unchecked box is `[]`, which is truthy — it would force the option on.
        mcqMode: Array.isArray(values["ctfMcqMode"]) && values["ctfMcqMode"].includes("mcq"),
        timeLimitSec: Number(values["ctfTimeLimitSec"] ?? 90),
        leaderboard: Array.isArray(values["ctfLeaderboard"]) && values["ctfLeaderboard"].includes("leaderboard"),
        // Membership test, not truthiness — !![] is true, so we must use .includes().
        sharedEvidence: Array.isArray(values["ctfSharedEvidence"]) && values["ctfSharedEvidence"].includes("evidence"),
      }
    : ctfFieldGroupWasShown
      ? { enabled: false }
      : undefined
  const ctfCoordinator =
    ctfExerciseSettings && typeof ctfExerciseSettings === "object"
      ? ctfCoordinatorFromExerciseSettings(ctfExerciseSettings, { groupGame: Boolean(isGroupGameSetup) })
      : legacyCtfCoordinator
  // The new-vs-classic UI is now a per-user preference, not a per-session option.
  // Always store false on the session for back-compat; routing ignores this field.
  const useNewGameplayExperience = false

  // Guidance mode applies to all exercise types; default to "assessment".
  const guidanceMode = values?.[MULTIPLAYER_FORM_FIELDS.GUIDANCE_MODE] || "assessment"
  // defaultLanguage is declared at the top of this function (it's read by the spectators/players
  // .map() callbacks above — a late declaration TDZ-crashed any exercise with an observer).

  if (isGroupGameSetup) {
    return {
      isGroup: true,
      params: {
        org: orgId,
        name,
        players: playerList,
        batchSize,
        scheduledTimeForFirstBatch,
        timeIntervalBetweenBatches,
        nlpContext,
        nlpEnabled,
        nlpMatchThreshold,
        nlpModel,
        nlpAutoDisableThreshold,
        roleAssignments,
        roleAssigneeNames,
        roleAssigneeTitles,
        roleAssigneeLanguages,
        spectators,
        variables,
        facilitator,
        facilitators,
        useNewGameplayExperience,
        executiveDetailsEnabled,
        guidanceMode,
        // Group (multi-player) exercises are precisely the case where the CTF
        // leaderboard matters; omitting this dropped the whole coordinator config.
        ctfCoordinator,
        defaultLanguage,
        soloRoles,
        otherRolesMode,
      },
    }
  }

  // Build players array (only for multi-player games)
  // This converts traditional role assignments to the new players array format
  let players = null // null for single-player games, array for multiplayer games
  const uniqueEmails = new Set()

  // Helper to add player if unique
  const addPlayer = (p) => {
    if (p.email && !uniqueEmails.has(p.email)) {
      if (!players) {
        players = [] // Initialize array only when needed (multiplayer games)
      }
      players.push(p)
      uniqueEmails.add(p.email)
    }
  }

  // Only populate players array for multiplayer games
  if (isMultiplayer) {
    // Facilitated roundtable: seats are virtual — one per scenario role, marked so the
    // server never sends them invites/nags and the facilitator can record for them.
    if (isFacilitated) {
      // Slug collisions ("IR Lead" vs "IR-Lead") would merge seats via addPlayer's email
      // de-dupe — suffix duplicates so every role keeps its own seat identity.
      const seatEmails = new Set()
      for (const scenarioRole of facilitatedSeatRoles) {
        let email = virtualSeatEmail(scenarioRole)
        let n = 2
        while (seatEmails.has(email)) {
          email = virtualSeatEmail(`${scenarioRole} ${n}`)
          n += 1
        }
        seatEmails.add(email)
        // Optional participant name for this seat (from the facilitated setup). When given it
        // becomes the seat's display name (list + report show the person); role/title keep the
        // role so displays can read "Alice — IT Lead". Blank falls back to the role, as before.
        const seatName = (values?.[paramNameForRole({ role: scenarioRole, param: "seatName" })] || "").trim()
        addPlayer({
          email,
          name: seatName || scenarioRole,
          title: scenarioRole,
          role: scenarioRole,
          virtual: true,
          language: defaultLanguage,
        })
      }
    }
    // 2. Add players from explicit multiplayer list (from new Players table) FIRST.
    // A row marked "Played by" a bot has no human identity — it maps to roleBots
    // (the autoplay AI teammate) and is not added as a human player.
    // FACILITATED is EXCLUDED: it runs virtual seats only (added above). Iterating formPlayers here
    // would add the roster people as REAL invited players — and a Collaborative→Facilitated format
    // switch now preserves that roster (details name-wipe fix), so those collaborative participants
    // (and their bot assignments) would otherwise be silently invited into a facilitator-only session.
    let botTeammateIndex = 0
    for (const player of isFacilitated ? [] : formPlayers) {
      // The row's typed Role is sent VERBATIM (comma-split, "All roles"/blank → none) — the SERVER
      // maps typed → canonical at save (det tiers + LLM): roleBots KEYS are mapped, and players[].role
      // gets a mappedRole sibling for seat routing. Display keeps exactly what was typed.
      const playerRoles = soloRolesFromRoleFieldValue(player.role)
      if (player.playAs && player.playAs !== "human") {
        // A bot row can cover SEVERAL seats — map the bot onto EVERY typed seat (the server maps each
        // key to a canonical seat). No seat typed → one synthetic key.
        const botSeats = playerRoles.map((r) => String(r).trim()).filter(Boolean)
        if (botSeats.length > 0) {
          for (const seat of botSeats) roleBots[seat] = player.playAs
        } else {
          roleBots[`AI Teammate ${++botTeammateIndex}`] = player.playAs
        }
      } else if (player.email && player.name) {
        // Keep players[].role a SCALAR (NewReport reads `player.title || player.role`).
        // Report label (player.title): the TYPED seat value(s) — what the setter wrote — joined for a
        // multi-seat person; the name is the fallback for a seatless participant. The server's
        // mappedRole (canonical) drives routing/scoring; display stays typed.
        const seatLabel = playerRoles
          .map((r) => String(r).trim())
          .filter(Boolean)
          .join(", ")
        addPlayer({
          email: player.email.toLowerCase(),
          name: player.name,
          title: seatLabel || player.name,
          // The sole TYPED seat when exactly one is typed, else null (the server maps it to canonical).
          role: playerRoles.length === 1 ? playerRoles[0] : null,
          // Every role they typed (blank / "All roles" → []): the server tells an unchanged person from a
          // changed one by it when an exercise is edited.
          typedRoles: playerRoles.map((r) => String(r).trim()).filter(Boolean),
          // "" (Exercise default) falls through to defaultLanguage — || not ??.
          language: normalizeLanguage(player.language || defaultLanguage),
        })
      }
    }

    // 1. Add role-assigned players (Primary - from traditional single-player forms)
    // Only add if they're not already in the multiplayer list
    for (const scenarioRole of scenarioRoles) {
      const email = roleAssignments[scenarioRole]
      const name = roleAssigneeNames[scenarioRole]
      const title = roleAssigneeTitles[scenarioRole]
      if (email && name) {
        addPlayer({
          email: email.toLowerCase(),
          name,
          title: title || scenarioRole,
          role: scenarioRole,
          // roleAssigneeLanguages holds only explicit per-role overrides; absent ⇒ default.
          language: normalizeLanguage(roleAssigneeLanguages[scenarioRole] || defaultLanguage),
        })
      }
    }

    // 3. For legacy multiplayer mode: add spectators with canPlay as additional players
    // Add players from explicit list. EXCLUDED for Facilitated: this reads the same multiplayer roster
    // (_multiplayerPlayers) as loop 2 and would re-invite the carried-over collaborative people as real
    // players into a virtual-seat-only facilitated session.
    const listedPlayers = isFacilitated ? [] : (multiplayerPlayersFrom({ values }) ?? [])
    for (const p of listedPlayers) {
      if (p.email && p.name) {
        // Default role strategy: use the first role available if single role
        const defaultRole = scenarioRoles.length === 1 ? scenarioRoles[0] : null
        addPlayer({
          email: p.email.toLowerCase(),
          name: p.name,
          title: p.title || p.name, // Use name as title if not provided
          role: defaultRole,
          language: normalizeLanguage(p.language || defaultLanguage),
        })
      }
    }

    // Add spectators with canPlay as additional players
    for (const spec of spectators) {
      if (spec.canPlay === true && spec.email && spec.name) {
        const defaultRole = scenarioRoles.length === 1 ? scenarioRoles[0] : null
        addPlayer({
          email: spec.email.toLowerCase(),
          name: spec.name,
          title: spec.name,
          role: defaultRole,
          language: normalizeLanguage(spec.language || defaultLanguage),
        })
      }
    }
  }

  // Chat is FORCED on for collaborative exercises (product direction 2026-09-05). Whether players
  // see each other's activity is the sponsor's choice again (product direction 2026-10-02, R9): the
  // Step-2 "Players see each other's activity" switch, default ON. It uses a NEW field
  // (_playersSeeEachOther) so a stale `_showOtherPlayersActions` in an old draft can't turn it off.
  // Non-collaborative setups keep the server default (neither surface exists for them).
  const chatEnabled = isMultiplayer ? true : undefined
  // Absent (the form never rendered the switch) → undefined: the server default applies on create and the
  // stored value is left alone on edit — never silently overwritten.
  // The Facilitated Roundtable has no such switch (one facilitator drives the room) — always shared.
  const showOtherPlayersActions = isFacilitated
    ? true
    : isMultiplayer && values?._playersSeeEachOther !== undefined
      ? values._playersSeeEachOther !== false
      : undefined
  // Collaborative sub-teams (R8) — only meaningful while players can see each other; cleared when the
  // switch is off; undefined (left as stored) when the form never carried them. Roles are kept only if
  // they are still scenario seats (the server re-validates the shape).
  // A Facilitated Roundtable has no sub-teams (one facilitator drives one shared room): clear any left over
  // from a Collaborative setup the form was switched away from, instead of serialising them.
  const subTeams = isFacilitated
    ? []
    : isMultiplayer
      ? showOtherPlayersActions === false
        ? []
        : Array.isArray(values?._subTeams)
          ? values._subTeams.map((t) => ({
              id: t?.id,
              name: String(t?.name ?? "")
                .replace(/\s+/g, " ")
                .trim(),
              roles: (t?.roles ?? []).filter((r) => scenarioRoles.some((s) => String(s).toLowerCase() === String(r).toLowerCase())),
            }))
          : undefined
      : undefined

  // Build params object
  // (sessionDisplayOptions / executiveDetailsEnabled computed above the group return)
  const params = {
    orgId,
    name,
    nlpContext,
    nlpEnabled,
    nlpMatchThreshold,
    nlpModel,
    nlpAutoDisableThreshold,
    roleAssignments,
    roleAssigneeNames,
    roleAssigneeTitles,
    roleAssigneeLanguages,
    spectators,
    variables,
    scheduledTime,
    dispatched: scheduledTime !== null ? false : true,
    executiveDetailsEnabled,
    useNewGameplayExperience,
    facilitator,
    facilitators,
    bots: sessionBots,
    guidanceMode,
    ctfCoordinator,
    defaultLanguage,
    soloRoles,
    otherRolesMode,
  }

  // COLLABORATIVE role-scoped scoring (user direction 2026-09-16): when the team staffs only SOME of
  // the scenario's seats, the shared score is gauged on the STAFFED roles' actions — unstaffed roles'
  // turns are auto-advanced (skip) or bot-played (autoplay when a seat has a bot). Staffing every
  // human seat == whole incident ([]). Legacy rosters with no explicit seat picks keep the old
  // whole-incident behavior untouched.
  // Facilitated Roundtable is EXCLUDED: it runs every seat as a virtual identity (whole incident),
  // so it must not be role-scoped by a (possibly stale) seat-picked roster carried over from a
  // Collaborative→Facilitated format switch. Only true Collaborative reaches this block.
  if (isMultiplayer && !isFacilitated && Array.isArray(formPlayers) && formPlayers.length > 0) {
    // The TYPED seat values are sent VERBATIM — the SERVER maps typed → canonical (det tiers + LLM)
    // at save and scores on soloRolesMapped (user direction 2026-09-18). The client's deterministic
    // resolver (resolveRowSeats) is used ONLY as an internal heuristic for coverage/otherRolesMode —
    // never to rewrite what the setter typed.
    const typedSeats = (pl) => soloRolesFromRoleFieldValue(pl?.role)
    const anyExplicitPick = formPlayers.some((pl) => typedSeats(pl).length > 0)
    if (anyExplicitPick) {
      const humanTyped = [...new Set(formPlayers.filter((pl) => !pl?.playAs || pl.playAs === "human").flatMap((pl) => typedSeats(pl)))]
      // Coverage/mode HEURISTIC on deterministically-resolved seats (exact/normalized/synonym): a seat
      // only the server's LLM tier can map counts as unstaffed here — worst case the mode leans
      // autoplay/skip conservatively; scoring is unaffected (the server scores on the mapped scope).
      const humanSeatsHeuristic = [
        ...new Set(formPlayers.filter((pl) => !pl?.playAs || pl.playAs === "human").flatMap((pl) => resolveRowSeats(pl?.role, pl?.email))),
      ]
      const coversAll = humanSeatsHeuristic.length > 0 && scenarioRoles.every((r) => humanSeatsHeuristic.includes(r))
      params.soloRoles = coversAll ? [] : humanTyped
      // Preliminary autoplay/skip from the roster-derived roleBots. AI Stakeholders (playerRoleBots)
      // are merged into roleBots LATER in email-setup, so this is recomputed there once roleBots is
      // final — see resolveCollaborativeOtherRolesMode.
      params.otherRolesMode = resolveCollaborativeOtherRolesMode({ scenarioRoles, humanSeats: humanSeatsHeuristic, roleBots })
    }
  }

  // Only include players array for multiplayer games (when it's not null/empty)
  if (players && players.length > 0) {
    params.players = players
  }

  // Per-role bot assignments (manager, multi-role). Sent only when non-empty so
  // non-manager / no-bot flows leave any existing assignment untouched.
  if (Object.keys(roleBots).length > 0) {
    params.roleBots = roleBots
  }

  // Only include multiplayer settings for multiplayer games
  if (isMultiplayer) {
    params.chatEnabled = chatEnabled
    if (showOtherPlayersActions !== undefined) params.showOtherPlayersActions = showOtherPlayersActions
    if (subTeams !== undefined) params.subTeams = subTeams
  }

  return {
    isGroup: false,
    isMultiplayer,
    isFacilitated,
    params,
  }
}

const companySetupVariables = () => {
  return [
    {
      name: "board_yes_no",
      type: "ENUM",
      setupQuestion: "Does your company have a Board of Directors?",
      enums: ["Yes", "No"],
      notes: null,
      default: null,
      created: null,
      updated: null,
    },
    {
      name: "instant_messaging_tool",
      type: "ENUM_WITH_STRING_OPTION",
      setupQuestion: "What is the main instant messaging tool you use?",
      enums: ["Google Chat", "Microsoft Teams", "Slack", "Zoom", "None"],
      notes: "",
      default: null,
      created: null,
      updated: null,
    },
    {
      name: "leadership_team_name",
      type: "ENUM_WITH_STRING_OPTION",
      setupQuestion: "What is the name of the senior-most committee that runs your business?",
      enums: ["Executive Committee", "Executives", "Leadership", "Partners"],
      notes: "",
      default: null,
      created: null,
      updated: null,
    },
    {
      name: "sensitive_info_types",
      type: "MULTI_ENUM_WITH_STRING_OPTION",
      setupQuestion: "What kind of sensitive information does your company store?",
      enums: ["Credit Card Numbers", "Social Security Numbers", "Driver's License Numbers", "Healthcare Information", "Bank Account Numbers"],
      notes: "",
      default: null,
      created: null,
      updated: null,
    },
  ]
}

export const companySetupLayout = ({ organization }) => {
  const columnOneLayoutGroups = []
  const columnTwoLayoutGroups = []
  let view_order = 10
  let company_detail = 0

  // Fixed Company Setup
  // Company Display Name
  const companyNameFields = []
  companyNameFields.push({
    id: `company-details-${++company_detail}`,
    width: 12,
    type: FIELD_TYPES.STRING,
    label: "Company Name",
    name: "_displayName",
    placeholder: "Enter Company Name",
    value: organization?.displayName ?? "",
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.REQUIRED,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
      },
      {
        type: VALIDATION_TYPES.SAFENAME,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
      },
      {
        type: VALIDATION_TYPES.MAX_LENGTH,
        value: validNameFieldMaxLength,
        error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
      },
    ],
    comments: "",
  })
  columnOneLayoutGroups.push({
    view_order,
    id: "company_name",
    type: FIELD_GROUP_TYPES.FIELDS,
    label: "",
    fields: companyNameFields,
  })
  // Administrator Name
  view_order += 10
  const adminNameFields = []
  adminNameFields.push({
    id: `company-details-${++company_detail}`,
    width: 12,
    type: FIELD_TYPES.STRING,
    label: `Administrator's Name`,
    name: "_sponsorName",
    placeholder: `Enter Administrator's Name`,
    value: organization?.sponsorName ?? "",
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.REQUIRED,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
      },
      {
        type: VALIDATION_TYPES.SAFENAME,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.SAFENAME,
      },
      {
        type: VALIDATION_TYPES.MAX_LENGTH,
        value: validNameFieldMaxLength,
        error_message: errorMessageReplacer(VALIDATION_TYPES.MAX_LENGTH, LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.MAX_LENGTH, validNameFieldMaxLength),
      },
    ],
    comments: "",
  })
  columnOneLayoutGroups.push({
    view_order,
    id: "company_admin_name",
    type: FIELD_GROUP_TYPES.FIELDS,
    label: "",
    fields: adminNameFields,
  })
  // Administrator Email
  view_order += 10
  const adminEmailFields = []
  adminEmailFields.push({
    id: `company-detail-${++company_detail}`,
    width: 12,
    type: FIELD_TYPES.STRING,
    label: "Administrator Email",
    name: "_sponsorEmail",
    placeholder: `Administrator's Email`,
    value: organization?.sponsorEmail ?? "",
    icon: null,
    options: [],
    validations: [
      {
        type: VALIDATION_TYPES.REQUIRED,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED,
      },
      {
        type: VALIDATION_TYPES.EMAIL,
        error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.EMAIL,
      },
    ],
    comments: "",
  })
  columnOneLayoutGroups.push({
    view_order,
    id: "company_admin_email",
    type: FIELD_GROUP_TYPES.FIELDS,
    label: "",
    fields: adminEmailFields,
  })
  // Setup Questions
  // const allVars = companySetupVariables() ?? []
  // allVars.forEach((scenarioVariable, idx, list) => {
  //   view_order += 10
  //   // Map the variable type to the layout field type
  //   const varGroup = {
  //     view_order,
  //     id: scenarioVariable.name,
  //     type: FIELD_GROUP_TYPES.FIELDS,
  //     label: scenarioVariable.setupQuestion,
  //     fields: [
  //       {
  //         id: `company-detail-${++company_detail}`,
  //         width: 12,
  //         type: layoutFieldTypeForVariableType(scenarioVariable.type),
  //         label: '',
  //         name: scenarioVariable.name,
  //         placeholder: '',
  //         value: layoutFieldValueForVariable({
  //           variable: scenarioVariable,
  //           rawVal: organization?.variables?.[scenarioVariable.name]?.value ?? ''
  //         }),
  //         icon: null,
  //         options: fieldOptionsForScenarioVariable(scenarioVariable),
  //         validations: [
  //           {
  //             type: VALIDATION_TYPES.REQUIRED,
  //             error_message: LAYOUT_FIELD_VALIDATION_ERROR_MESSAGE.REQUIRED
  //           }
  //         ],
  //         comments: ''
  //       }
  //     ]
  //   }
  //   // Split between first and second columns
  //   if (idx < (list.length / 2)) {
  //     columnOneLayoutGroups.push(varGroup)
  //   } else {
  //     columnTwoLayoutGroups.push(varGroup)
  //   }
  // })
  return {
    layout: "1-COLUMN",
    layout_data: [
      {
        id: "layout-1",
        layout_order: 10,
        layout_width: 6,
        groups: columnOneLayoutGroups,
      },
      // {
      //   id: 'layout-2',
      //   layout_order: 20,
      //   layout_width: 6,
      //   groups: columnTwoLayoutGroups
      // }
    ],
  }
}

export const orgParametersFromCompanyDetailsValues = ({ values }) => {
  // Fixed Company Setup
  // Display Name, Admin Name, Admin Email
  const displayName = values?._displayName ?? ""
  const sponsorName = values?._sponsorName ?? ""
  const sponsorEmail = values?._sponsorEmail ?? ""

  // Setup Questions / Variables
  const variables = {}
  for (const orgVar of companySetupVariables() ?? []) {
    const name = orgVar.name
    const defaultValue = orgVar.default
    const type = orgVar.type
    const otherVal = values?.[`other_${orgVar.name}`]
    let rawVal = values?.[orgVar.name]
    let val
    if (Array.isArray(rawVal)) {
      // Swap in the "Other" value (if necessary)
      rawVal = rawVal
        .map((origVal) => {
          return origVal.toLowerCase() === OTHER_OPTION_PLACEHOLDER_VALUE.toLowerCase() ? otherVal : origVal
        })
        .filter((val) => val !== "") // Remove empty values
      rawVal = rawVal.join(",")
      val = rawVal
    } else {
      val = otherVal || rawVal || (defaultValue ?? "")
    }

    variables[name] = {
      type,
      value: val,
    }
  }

  return {
    displayName,
    sponsorName,
    sponsorEmail,
    variables,
  }
}

export const getSessionsStatusFilterParamFor = (uiFilterEnum) => {
  let filterParam = "ALL"
  switch (uiFilterEnum) {
    case TABLE_GAME_STATUS_FILTER.ONGOING:
      filterParam = "ONGOING"
      break
    case TABLE_GAME_STATUS_FILTER.FINISHED:
      filterParam = "FINISHED"
      break
    case TABLE_GAME_STATUS_FILTER.PAUSED:
      filterParam = "PAUSED"
      break
    case TABLE_GAME_STATUS_FILTER.SCHEDULED:
      filterParam = "SCHEDULED"
      break
    default:
      break
  }
  return filterParam
}

const getSessionsArchivedParamFor = (archivedFilterEnum) => {
  let filterParam = "SHOW"
  switch (archivedFilterEnum) {
    case TABLE_GAME_ARCHIVED_FILTER.HIDE:
      filterParam = "HIDE"
      break
    case TABLE_GAME_ARCHIVED_FILTER.SHOW:
      filterParam = "SHOW"
      break
    default:
      break
  }
  return filterParam
}

const getSessionsTimeFilterParamFor = (uiFilterEnum) => {
  let filterParam = "ALL"
  switch (uiFilterEnum) {
    case TABLE_GAME_TIME_FILTER.LAST_WEEK:
      filterParam = "LAST_WEEK"
      break
    case TABLE_GAME_TIME_FILTER.LAST_MONTH:
      filterParam = "LAST_MONTH"
      break
    case TABLE_GAME_TIME_FILTER.LAST_YEAR:
      filterParam = "LAST_YEAR"
      break
    case TABLE_GAME_TIME_FILTER.YEAR_TO_DATE:
      filterParam = "YEAR_TO_DATE"
      break
    default:
      break
  }
  return filterParam
}

const getSessionsSortFilterParamFor = (uiFilterEnum) => {
  let filterParam = "NONE"
  switch (uiFilterEnum) {
    case TABLE_GAME_GROUP_BY_FILTER.GAME_STATUS:
      filterParam = "STATUS"
      break
    case TABLE_GAME_GROUP_BY_FILTER.GAME_SCENARIO:
      filterParam = "SCENARIO_NAME"
      break
    default:
      break
  }
  return filterParam
}

const getSessionsIimeSortParamFor = (timeSortParam) => {
  let filterParam = sessionTimeSortParms.NONE
  switch (timeSortParam) {
    case TABLE_SORT_ORDER.ASC:
      filterParam = sessionTimeSortParms.ASC
      break
    case TABLE_SORT_ORDER.DESC:
      filterParam = sessionTimeSortParms.DESC
      break
    default:
      break
  }
  return filterParam
}

export const convertToSessionsFilterParams = ({ filterData, searchInput = undefined }) => {
  const gameStatusFilter = filterData?.[TABLE_GAME_FILTERS.GAMES_STATUS]
  const gameTimeFilter = filterData?.[TABLE_GAME_FILTERS.TIME]
  const gameGroupByFilter = filterData?.[TABLE_GAME_FILTERS.GROUP_BY]
  const gameTimeSortParam = filterData?.[TABLE_GAME_FILTERS.TIME_SORT]
  const gameArchivedParam = filterData?.[TABLE_GAME_FILTERS.ARCHIVED]

  const params = {
    sort: getSessionsSortFilterParamFor(gameGroupByFilter),
    timeSort: getSessionsIimeSortParamFor(gameTimeSortParam),
    timeFilter: getSessionsTimeFilterParamFor(gameTimeFilter),
    statusFilter: getSessionsStatusFilterParamFor(gameStatusFilter),
    searchInput,
    archived: getSessionsArchivedParamFor(gameArchivedParam),
  }
  pageLogger.info({ params }, "convertToSessionsFilterParams")
  return params
}

export const generalizeRoleNamesForVariables = ({ variables = {}, roleAssignments = {} }) => {
  // Build 'generic' participant name variable set based on the role titles
  const genericParticipantNameVariables = { ...variables }

  // For each role, map the '_name' variable value to the '_title' variable value
  for (const roleKey of Object.keys(roleAssignments)) {
    // Get the role title variable
    const roleTitle = genericParticipantNameVariables[`${paramNameForRole({ role: roleKey, param: "title" })}`] ?? roleKey
    // Set the role name variable
    genericParticipantNameVariables[`${paramNameForRole({ role: roleKey, param: "name" })}`] = roleTitle
  }
  return genericParticipantNameVariables
}

const MAX_RELATED_HISTORICAL_SESSIONS = 5

export const fetchRelatedHistoricalSessionsForSession = async ({ userId, accessToken, session }) => {
  const relatedSessions = []
  const test = session?.reportData?.reportItems?.scenarioId
  if (test && session?.org && userId && accessToken) {
    const searchResults =
      (await getSessionGroupReports(accessToken, userId, {
        test,
        org: session.org,
        // statusFilter: getSessionsStatusFilterParamFor(TABLE_GAME_STATUS_FILTER.FINISHED),
        timeSort: sessionTimeSortParms.ASC,
        includeUngrouped: true,
      })) ?? []
    // Trim related groups down to a 'window' of 5 games surrounding the 'session' itself, preferring the most recent related games
    const thisSessionDispatchTime = session.dispatchTime
    let previousGameCount = 0
    let concurrentGameCount = 0 // The selected game and any other 'concurrent' game (unlikely to be >1)
    let followingGameCount = 0

    // Only include other standalone (ungrouped) games
    const relatedSessionIds = searchResults?.find((aSessionGroupResult) => aSessionGroupResult.name === null)?.sessionNames ?? []

    const allRelatedSessions = (await Promise.all(relatedSessionIds.map((aSessionId) => getSession(accessToken, userId, { name: aSessionId }))))
      .map((aResult) => aResult.result)
      .filter((aSession) => !!aSession?.dispatchTime) // Only include dispatched games

    allRelatedSessions.sort((a, b) => a.dispatchTime - b.dispatchTime)

    for (const relatedSession of allRelatedSessions) {
      const relatedDispatchTime = relatedSession.dispatchTime
      if (relatedDispatchTime < thisSessionDispatchTime) {
        previousGameCount += 1
      } else if (relatedDispatchTime > thisSessionDispatchTime) {
        followingGameCount += 1
      } else {
        concurrentGameCount += 1
      }
    }

    let trimmedResults

    switch (true) {
      case previousGameCount + followingGameCount + concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS:
        trimmedResults = allRelatedSessions // show all results
        break
      case followingGameCount + concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS: {
        const startIndex = previousGameCount + followingGameCount + concurrentGameCount - MAX_RELATED_HISTORICAL_SESSIONS
        trimmedResults = allRelatedSessions.slice(startIndex) // trim older games from results
        break
      }
      case concurrentGameCount <= MAX_RELATED_HISTORICAL_SESSIONS: {
        const startIndex = previousGameCount
        const endIndex = previousGameCount + MAX_RELATED_HISTORICAL_SESSIONS
        trimmedResults = allRelatedSessions.slice(startIndex, endIndex) // trim older games first, then trim (minimal) newer games
        break
      }
      default: {
        const startIndex = previousGameCount
        const endIndex = previousGameCount + concurrentGameCount
        trimmedResults = allRelatedSessions.slice(startIndex, endIndex) // only return concurrent games
        break
      }
    }

    relatedSessions.push(...trimmedResults)
  }
  return relatedSessions
}

const BOT_RISK_LEVELS = new Set(["cautious", "balanced", "bold"])

// The botRisk sent with a launch / edit: the wizard's react and stakeholder settings, plus the saved
// setting of bots the wizard keeps but doesn't show (inject bots, legacy keys) — otherwise an unrelated
// edit would reset theirs, because the server replaces the stored map.
export const launchBotRisk = ({ savedBotRisk = null, preservedKeys = [], reactBotRisk = {}, playerBotRisk = {} }) => {
  const saved = savedBotRisk && typeof savedBotRisk === "object" ? savedBotRisk : {}
  const preserved = Object.fromEntries(preservedKeys.filter((key) => BOT_RISK_LEVELS.has(saved[key])).map((key) => [key, saved[key]]))
  return { ...preserved, ...(reactBotRisk ?? {}), ...(playerBotRisk ?? {}) }
}

// Bots in the final saved list that the react region doesn't manage (not a react library key, not a
// custom bot): inject bots from the inject picker and legacy keys. Their saved risk is kept on edit.
export const retainedNonReactBotKeys = ({ finalBots = [], reactLibKeys = new Set() }) =>
  (Array.isArray(finalBots) ? finalBots : []).map(String).filter((key) => !key.startsWith("custom:") && !reactLibKeys.has(key))
